import { alignmentsAtom, alignmentWalkAtom, DEFAULT_WALK_STEP } from '@/atoms/alignment.atom';
import { deviationTrackingAtom } from '@/atoms/deviation-progress.atom';
import { layerDefaultSettings, layerSettingsAtom, layersAtom } from '@/atoms/layers.atom';
import {
  DOCK_NARROW_PX,
  DOCK_VERY_NARROW_PX,
  sectionOverlaySettingsAtom,
  stationBandsPinnedLayerAtom,
  stationBandsVisibleAtom,
  stationClipToBinAtom,
  stationVolumeDockAtom,
  stationVolumeDockExpandedAtom,
  stationVolumeSelectionAtom,
  type StationVolumeTab,
} from '@/atoms/stationVolumes.atom';
import { clipBoxStateAtom, viewerEvents, type ClipBoxState } from '@/atoms/viewer.atom';
import { DockDetailsPopover, type DockDetailsSection } from '@/components/dock/DockDetailsPopover';
import { DockExportMenu, DockMenuItem, DockMenuSeparator, DockOverflowMenu } from '@/components/dock/DockExportMenu';
import { DockBody, DockFrame } from '@/components/dock/DockFrame';
import {
  DockHeader,
  DockIdentity,
  DockKpiStrip,
  DockPanelButtons,
  type DockKpiItem,
} from '@/components/dock/DockHeader';
import { DockChip, DockKpi } from '@/components/dock/DockHeaderParts';
import { DockSelectionChip } from '@/components/dock/DockSelectionChip';
import { DockTabBar, type DockTab } from '@/components/dock/DockTabBar';
import { DockToolbar, DockToolbarSeparator } from '@/components/dock/DockToolbar';
import { DockMenuSection, DockMenuSwitch, DockViewMenu } from '@/components/dock/DockViewMenu';
import { DOCK_TEXT } from '@/components/dock/dockStyles';
import { Button } from '@/components/ui/button';
import { LoadingBadge } from '@/components/ui/LoadingBadge';
import { clipBoxAtStation, internalToDisplayRaw, type Alignment, type ClipDims } from '@/core/alignment';
import { useFeature, useFeatureCheck } from '@/hooks/useFeature';
import type { OpenAnalysisReportArgs } from '@/hooks/useOpenAnalysisReport';
import { execute, getLinkSessionId } from '@/graphql/execute';
import { ProcessJobType } from '@/graphql/graphql';
import {
  sectionRowAt,
  useLayerStack,
  useSectionOverlays,
  useStationOffsets,
  useStationSections,
  useStationVolumes,
} from '@/hooks/useStationVolumes';
import { downloadFile } from '@/lib/download';
import {
  STACK_LAYERS_CSV_FILENAME,
  STACK_STATIONS_CSV_FILENAME,
  stackLayersCsv,
  stackStationsCsv,
} from '@/lib/layerStack/csv';
import { rollUpCriteria, stackDockData, stackLayerChips } from '@/lib/layerStack/dockModel';
import type { LayerStackLayerMeta } from '@/lib/layerStack/layerMeta';
import { layerStackReportScope } from '@/lib/layerStack/reportScope';
import {
  binCentreStation,
  binIndexForStation,
  clipDimsForRun,
  totalsForBins,
  totalsForRange,
  type RangeTotals,
} from '@/lib/stationVolumes/aggregate';
import { alignmentHashMatches } from '@/lib/stationVolumes/alignmentInput';
import { lookAtSectionPose } from '@/lib/stationVolumes/sectionOverlay';
import { SWATH_UNDERUSED_PCT, fittedSwathFromExtents } from '@/lib/stationVolumes/fitSwath';
import {
  formatFixed,
  formatM3Row,
  formatPct,
  formatStationRange,
  STATION_VOLUME_DISPLAY_DECIMALS,
} from '@/lib/stationVolumes/format';
import {
  MAX_STATION_PAGES,
  perStationPdfScope,
  perStationPdfStackBlock,
  stationRendersAvailable,
} from '@/lib/stationVolumes/perStationPdf';
import { dxfSeriesForLine, type SectionInput } from '@/lib/stationVolumes/sectionDxf';
import {
  asOverlayTranslate,
  overlayLabel,
  overlayLabelText,
  overlaysMatchRun,
  overlayStates,
  sectionOverlaysHash,
  type OverlayState,
  type SectionOverlayKind,
  type SectionOverlaySetting,
} from '@/lib/stationVolumes/sectionOverlays';
import { overlaySeries, runSeries, seriesRowsAt } from '@/lib/stationVolumes/sectionSeries';
import {
  stationExportFilename,
  stationSectionsFilename,
  stationSectionsToCsv,
  stationVolumesToCsv,
  stationVolumesToTsv,
  type StationExportOptions,
} from '@/lib/stationVolumes/stationExport';
import { useSvPalette } from '@/lib/stationVolumes/useSvPalette';
import { StationBandsLegend } from './StationBandsLegend';
import type { StationVolumesInitialParams } from '@/lib/stationVolumes/dialogModel';
import type {
  SectionOverlaysLayerMeta,
  StationBin,
  StationCorridorMeta,
  StationVolumesLayerMeta,
} from '@/lib/stationVolumes/types';
import { useDialog } from '@/providers/DialogProvider';
import {
  layerSettingsQuery,
  materializeSectionOverlaysMutation,
  updateLayerSettingsMutation,
} from '@/queries/layer.query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom, useStore } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { LayerComplianceStrip } from './LayerComplianceStrip';
import { SectionOverlaysPopover } from './SectionOverlaysPopover';
import { StationSectionChart } from './StationSectionChart';
import { StationVolumeChart } from './StationVolumeChart';
import { StationSectionDxfDialog } from './StationSectionDxfDialog';
import { StationVolumeTable } from './StationVolumeTable';
import { saveStripPng, StripMapTab, useStripSettings } from './StripMapTab';
import type { SurfaceDifferenceMeta } from './SurfaceDifferencePanel';
import { VerdictChip } from './VerdictChip';
import { openCompareSurfacesRerun } from '@/lib/analyses/rerunOpeners';
import { analysisGate } from '@/lib/analyses/registry';
import { fetchAnalysisRunInput } from '@/lib/analyses/runInput';
import { buildRerunPlan, type RerunPlan } from '@/lib/analyses/rerun';

/** The deviation run's parameters as postprocess echoes them onto the derived
 *  cloud. Every field is optional: the block is written by whichever worker
 *  build made the layer, and older ones wrote fewer keys. Mirrors the subset
 *  `buildRerunPlan` (`lib/analyses/rerun.ts`) restores from `inputJson.params`. */
interface DeviationRunParams {
  mode?: 'vertical' | 'normal' | 'nearest' | 'horizontal';
  min?: number;
  max?: number;
  decimate?: number;
  colormap?: string;
  renderColormap?: string;
  verticalThreshold?: number;
  kNearestTriangles?: number;
  signed?: boolean;
  ifcName?: string;
  ifcTypes?: string;
}

/** The slice of a derived layer's metadata this dock reads. Typed rather than
 *  `as any` (ruling G5/T10.1) — `Layer.metadata` is an untyped JSON scalar. */
interface DerivedSurfaceLayerMetadata {
  batchId?: string;
  /** `[topLayerId, bottomLayerId]` (surface difference), written by postprocess. */
  derivedFromLayers?: string[];
  /** The job that made the layer. A surface-difference re-run reads the run's
   *  input back through it: the metadata carries no surface picks, sampling
   *  or name. */
  sourceProcessJobId?: string;
  /** Deviation: the source point cloud and the reference, for the re-run. */
  derivedFrom?: string;
  referenceLayerId?: string;
  /** Deviation: the run's own parameters, echoed here by postprocess
   *  (`postprocess.ts` :: `metadata.deviationParams = devCtx.params`). Without
   *  them a "re-run with new parameters" silently reverts mode, range and
   *  colour ramp to the dialog's defaults, and the two runs are no longer
   *  comparable. */
  deviationParams?: DeviationRunParams;
  /** Deviation display range written by postprocess for the coloured cloud. */
  deviationRange?: [number, number];
  /** Written by BOTH analyses from spec §10.3 on — read this first. */
  stationVolumes?: StationVolumesLayerMeta | null;
  /**
   * The ONE result layer a LAYER_STACK run produces (spec §6.2). Present
   * instead of — never alongside — `stationVolumes`: a stack has no
   * `station_volumes.json`, its per-station bins live inside the stack
   * document, one array per LAYER, and the chip strip picks which.
   */
  layerStack?: LayerStackLayerMeta | null;
  /** Read fallback for surface-difference runs made before §10.3. */
  surfaceDifference?: SurfaceDifferenceMeta | null;
  /**
   * `postprocess.ts :: metadata.deviationStats = devCtx.stats` — the whole
   * raw stats blob `deviation.py` prints (`DeviationVolumePanel.tsx` already
   * reads the sibling `.volume` sub-object off it the same way). `.corridor`
   * is the block deviation's own worker merges in from the
   * `station_corridor.json` sidecar (Task 8); only its `expectation`
   * sub-block is read here — the corridor-scoped conformance verdict, the
   * ONLY headline a deviation run has (surface difference's own is grid-wide,
   * at `sd.stats.expectation`, rendered by `SurfaceDifferencePanel` — which
   * never mounts for a deviation run).
   */
  deviationStats?: { corridor?: StationCorridorMeta | null } | null;
  /**
   * What the SECTION_OVERLAYS worker last sampled onto this run's grid, and
   * — crucially — which settings list it sampled FOR (section-overlays spec
   * §3). Absent on every layer with no overlay configured, which is every
   * layer made before the feature existed.
   */
  sectionOverlays?: SectionOverlaysLayerMeta | null;
}

export interface StationVolumeDockProps {
  projectId: string;
  /**
   * Opens `GenerateReportDialog` with the section this menu item asked for
   * (ruling T10.6 / T15.6, plus spec §7's layer-stack compliance report).
   * `Viewer.tsx` always supplies it, via `useOpenAnalysisReport` — it is
   * optional only so the dock can be mounted bare in a test. The menu items
   * are therefore always live; they no longer carry a "coming soon" line,
   * which is what one said for the whole of the wiring task and nothing after.
   *
   * `stationFrom` / `stationTo` are INTERNAL stations covering the brushed
   * selection, `null` for the whole corridor; `stationRangeLabel` is the same
   * span in display stations, for the dialog's scope line. The full argument
   * shape — and, more to the point, WHICH document each `mode` produces —
   * lives on `OpenAnalysisReportArgs`, so the dock and the handler cannot
   * drift into disagreeing about it.
   */
  onOpenPdfReport?: (args: OpenAnalysisReportArgs) => void;
}

/**
 * The GraphQL row → the client model. The generated type has `ref?: string |
 * null` and an optional `__typename`; every consumer downstream (the chart,
 * the popover, `sectionOverlaysHash`, `overlaySeries`) takes one shape, so the
 * mapping happens at this ONE boundary and the generated type goes no further.
 */
const toClientOverlays = (
  raw:
    | readonly {
        id: string;
        kind: SectionOverlayKind;
        layerId: string;
        ref?: string | null;
        pcMethod?: string | null;
        hidden: boolean;
      }[]
    | null
    | undefined,
): SectionOverlaySetting[] =>
  (raw ?? []).map((o) => ({
    id: o.id,
    kind: o.kind,
    layerId: o.layerId,
    ref: o.ref ?? null,
    pcMethod: o.pcMethod ?? null,
    hidden: o.hidden,
  }));

/**
 * The station-volumes bottom dock: one instrument reading header (what was
 * measured, over what range, totalling what) → tab bar → chart / table /
 * section / strip. How it was measured — the method, the conventions, the
 * scope sentence the footer used to carry — is one click away in the header's
 * ⓘ Details popover (round-3 spec §B2: no footer). It is a bottom dock and not
 * a right-hand panel because a station chart needs width, and the viewer's
 * right column is already full (spec §4b).
 *
 * The chrome is the shared dock kit (`components/dock/`), the same frame,
 * header and tab bar `ClearanceDock` is built from, so the two docks that
 * share this slot cannot drift apart again (audit A2).
 */
export const StationVolumeDock = ({ projectId, onOpenPdfReport }: StationVolumeDockProps) => {
  const { t } = useTranslation('project');
  const { openDialog } = useDialog();
  // Subscription gate for the two surfaces spec §4g puts behind Pro: the
  // cross-section tab and the 3D corridor bands. Both stay in the DOM, focusable
  // and clickable, and open the upgrade dialog — CLAUDE.md forbids the HTML
  // `disabled` attribute here precisely because it swallows that click.
  const gate = useFeature('analysis.stationVolumes');
  // "Clip to station" drives the shared clip box, so it sits behind that
  // feature as well as this one — whichever is locked is the one the click
  // offers to unlock.
  // A shared link gets no exports, PDFs or re-runs (the PDFs and re-runs are
  // EDITOR jobs it can't start); copying the table stays. The clip box only
  // changes the view, so it is never paywalled there (see `clipBoxGate` in
  // Viewer.tsx).
  const isSharedLink = !!getLinkSessionId();
  const clipFeature = useFeature('viewer.clipBox');
  const clipGate = isSharedLink ? { ...clipFeature, allowed: true } : clipFeature;
  // The re-run opens a *run* dialog, so it is the run's own gate that
  // applies there — not this dock's. Imperative, because the decision is
  // made in a click handler (same shape as `useLaunchAnalysis`'s gate check).
  const featureCheck = useFeatureCheck();
  const [dock, setDock] = useAtom(stationVolumeDockAtom);
  const [expanded, setExpanded] = useAtom(stationVolumeDockExpandedAtom);
  const [selection, setSelection] = useAtom(stationVolumeSelectionAtom);
  const layers = useAtomValue(layersAtom);
  const alignments = useAtomValue(alignmentsAtom);
  const [walk, setWalk] = useAtom(alignmentWalkAtom);
  const [bandsOn, setBandsOn] = useAtom(stationBandsVisibleAtom(dock?.layerId ?? '__none'));
  const [pinnedBandsLayerId, setPinnedBandsLayerId] = useAtom(stationBandsPinnedLayerAtom);
  const bandsPinned = !!dock && pinnedBandsLayerId === dock.layerId;
  const [clipToBin, setClipToBin] = useAtom(stationClipToBinAtom(dock?.layerId ?? '__none'));
  // The clip box is read and written, never subscribed to: the walk toolbar
  // rewrites it once per animation frame while the user steps, and a
  // subscription here would re-render the whole dock on every one of those.
  const store = useStore();
  // The reference-overlay inks. Shared with the chart, which assigns the same
  // array in the same order — a DXF whose reference layers were coloured from
  // a different palette than the drawing on screen is a different drawing.
  const palette = useSvPalette();
  // Wide enough that the chart tab is available before the first measurement;
  // the ResizeObserver below corrects it on the first frame.
  const [width, setWidth] = useState(1200);
  // The frame's root, as STATE rather than a ref: the frame only mounts once
  // the run's metadata is in hand, which can be a render after the dock
  // opened, and an effect keyed on the layer id alone would by then have run
  // against a null ref and never observed anything.
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  /** The tab bar's Section-contextual slot, which the section chart portals
   *  its exaggeration picker into. State, for the same reason as `rootEl`. */
  const [sectionToolsEl, setSectionToolsEl] = useState<HTMLDivElement | null>(null);

  const layer = layers.find((l) => l.id === dock?.layerId);
  const layerMeta = (layer?.metadata ?? null) as DerivedSurfaceLayerMetadata | null;
  const sd = layerMeta?.surfaceDifference ?? null;
  // `metadata.stationVolumes` is where both analyses write it now; the nested
  // surface-difference key stays a read fallback for older runs (spec §10.3).
  const svMeta: StationVolumesLayerMeta | null = layerMeta?.stationVolumes ?? sd?.stationVolumes ?? null;
  const batchId = layerMeta?.batchId;

  // ---- The multi-layer stack -------------------------------------------
  //
  // A LAYER_STACK run puts ONE layer in the tree (spec §6.2) describing N−1
  // layers of material. This dock is EXTENDED for it rather than forked:
  // layer thickness is N−1 instances of what it already renders, on one grid
  // and one alignment, and the chip strip below is the selector. Everything
  // downstream of `meta`/`json`/`bins` is unchanged.
  const stackMeta = layerMeta?.layerStack ?? null;
  const {
    data: stackDoc,
    isLoading: stackLoading,
    error: stackError,
    progress: stackProgress,
    refetch: stackRefetch,
  } = useLayerStack(dock?.layerId ?? null, stackMeta, batchId);
  const [activeLayerIndex, setActiveLayerIndex] = useState(0);
  // A different run has different layers; layer 3 of the old stack is not
  // layer 3 of the new one. Reset on the LAYER, not on the document, so a
  // refetch of the same run keeps the reader where they were.
  useEffect(() => setActiveLayerIndex(0), [dock?.layerId]);
  const stackChips = useMemo(() => stackLayerChips(stackMeta, stackDoc), [stackMeta, stackDoc]);
  const stack = useMemo(
    () => stackDockData(stackMeta, stackDoc, activeLayerIndex),
    [stackMeta, stackDoc, activeLayerIndex],
  );
  const stackRollUpCriteria = useMemo(
    () => rollUpCriteria(stackDoc, stackDoc?.rollUp ?? stackMeta?.rollUp ?? null),
    [stackDoc, stackMeta],
  );

  const meta: StationVolumesLayerMeta | null = stack?.meta ?? svMeta;
  const kind = meta?.kind ?? 'surfaceDifference';
  /**
   * The corridor-scoped verdict (Task 14, Step 3b) — present only on a
   * deviation run that requested station volumes (Task 8). `SurfaceDifferencePanel`
   * never mounts for a deviation run, so this dock is the only place a
   * deviation user ever sees whether their layer passed.
   */
  const corridorExpectation = layerMeta?.deviationStats?.corridor?.expectation ?? null;
  /**
   * The section view's tolerance overlay (Task 15, spec §8.2) needs only the
   * four scalar inputs `toleranceRuns` compares each column against — mode,
   * design thickness and the two tolerances — not either summary block's own
   * share/verdict figures. Deviation carries them on `corridorExpectation`
   * above; surface difference has no corridor-scoped echo of its own (only
   * DEVIATION's `station_corridor.json` sidecar gets one — see
   * `StationCorridorMeta`'s docstring in `lib/stationVolumes/types.ts`), so a
   * surface-difference run falls back to the grid-wide `stats.expectation`
   * (`ExpectationMeta`): the run's chosen mode/thickness/tolerances are the
   * same numbers whichever scope reports them — only the SHARE figures
   * differ by scope, and the section computes its own per-column shares
   * straight from `zTop`/`zBottom`, not from either summary.
   */
  const sectionExpectation = corridorExpectation ?? sd?.stats?.expectation ?? null;
  const renderRange =
    Array.isArray(layerMeta?.deviationRange) &&
    typeof layerMeta.deviationRange[0] === 'number' &&
    typeof layerMeta.deviationRange[1] === 'number'
      ? layerMeta.deviationRange
      : typeof layerMeta?.deviationParams?.min === 'number' && typeof layerMeta.deviationParams.max === 'number'
        ? ([layerMeta.deviationParams.min, layerMeta.deviationParams.max] as [number, number])
        : null;
  const renderColormap =
    typeof layerMeta?.deviationParams?.renderColormap === 'string'
      ? layerMeta.deviationParams.renderColormap
      : typeof layerMeta?.deviationParams?.colormap === 'string'
        ? layerMeta.deviationParams.colormap
        : null;

  const {
    data: svJson,
    isLoading: svLoading,
    error: svError,
    progress: svProgress,
    refetch: svRefetch,
  } = useStationVolumes(dock?.layerId ?? null, svMeta, batchId);

  // ONE fetch state for the body, whichever document this layer has. The
  // stack path never reaches `useStationVolumes` (its synthesised meta has
  // `byproducts.json === null`, which disables the query) and the single-pair
  // path never reaches `useLayerStack`, so the two are mutually exclusive by
  // construction rather than by a flag anyone has to keep in step.
  const json = stack ? stack.json : svJson;
  const isLoading = stackMeta ? stackLoading : svLoading;
  const error = stackMeta ? stackError : svError;
  const progress = stackMeta ? stackProgress : svProgress;
  const refetch = stackMeta ? stackRefetch : svRefetch;

  // Measure the dock, not the window: the chart's super-binning threshold and
  // the responsive collapse both key off the actual plot width, and the dock
  // is narrower than the window whenever a side panel is open.
  useEffect(() => {
    if (!rootEl || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setWidth(w);
    });
    ro.observe(rootEl);
    return () => ro.disconnect();
  }, [rootEl]);

  // Below 520 px a chart has no room; show the table rather than a chart
  // nobody can read. The gate case is the same shape: a dock left on the
  // Section tab (a deep link, or a subscription that lapsed mid-session) falls
  // back to the table rather than rendering a surface the user has not paid
  // for.
  //
  // `dock.dataset === 'volumes'` is part of the condition, not just of the
  // render below (finding F-23, mirroring `ClearanceDock`'s own M8 guard):
  // both docks share this atom and mount unconditionally, and `width` is
  // component state that survives the OTHER dock taking over the slot.
  // Without the guard, opening this dock narrow, closing it, widening the
  // window and then opening the CLEARANCE dock would retab clearance onto
  // Table from this dock's stale measurement.
  useEffect(() => {
    if (!dock || dock.dataset !== 'volumes') return;
    if (width < DOCK_VERY_NARROW_PX && dock.tab === 'chart') setDock({ ...dock, tab: 'table' });
    // The Section AND Strip tabs are the two gated panes (spec §4g / strip-map
    // spec §3.1): losing the gate must land the reader somewhere they can read.
    else if (!gate.allowed && (dock.tab === 'section' || dock.tab === 'strip')) setDock({ ...dock, tab: 'table' });
  }, [width, dock, gate.allowed, setDock]);

  const bins: StationBin[] = useMemo(() => json?.bins ?? [], [json]);
  const sel = selection && dock && selection.layerId === dock.layerId ? selection : null;

  const totals: RangeTotals | null = useMemo(() => {
    if (!meta) return null;
    // Before the bin array lands, the summary block on layer metadata already
    // knows the corridor totals — show them rather than three em dashes.
    if (bins.length === 0) {
      return {
        cut: meta.summary.cutVolumeM3,
        fill: meta.summary.fillVolumeM3,
        net: meta.summary.netVolumeM3,
        binCount: meta.summary.binCount,
        lowCoverageCount: meta.summary.lowCoverageBinCount,
        lengthM: meta.params.stationToInternalM - meta.params.stationFromInternalM,
        fromInternalM: meta.params.stationFromInternalM,
        toInternalM: meta.params.stationToInternalM,
        // NOT `meta.summary.meanCoveragePct`: that is a plain mean over bins
        // (ruling A5), while every coverage figure the dock shows once the
        // bins land is cell-weighted. Printing one and then the other makes
        // the number visibly change for no reason the reader can see, so the
        // pre-load state says "unknown" instead (ruling A15, finding 10).
        coveragePct: null,
        swathUsedPct: null,
      };
    }
    return sel ? totalsForRange(bins, sel.fromBin, sel.toBin) : totalsForBins(bins);
  }, [bins, sel, meta]);

  const liveAlignment: Alignment | undefined = meta ? alignments.get(meta.alignment.key) : undefined;
  // PLAN, matching the mode `stationVolumesPayload` stamped the run with:
  // draping a profile-less alignment must not read as "the corridor moved".
  const hashOk = meta ? alignmentHashMatches(meta.alignment, liveAlignment, 'PLAN') : true;

  const close = useCallback(() => {
    setDock(null);
    setSelection(null);
    // The slab takes over the viewer's clip box — shared state — so reopening
    // the dock has to ask again rather than silently re-slicing the cloud
    // (ruling D36). The 3D bands stay sticky because they touch nothing shared.
    setClipToBin(false);
  }, [setDock, setSelection, setClipToBin]);

  // Finding F-24: a clearance chip REPLACES the shared `dock` atom (a
  // different `layerId`, `dataset: 'clearance'`) rather than calling
  // `close()`, so the reset above never runs and `stationClipToBinAtom` for
  // the volumes layer that was open stays latched `true` on its own
  // atomFamily entry. Track which layerId THIS dock last owned — independent
  // of `dock` itself moving on to the clearance dataset — so the reset below
  // still lands on that entry rather than on whatever layerId `dock` now
  // holds.
  const ownVolumesLayerIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (dock?.dataset === 'volumes') ownVolumesLayerIdRef.current = dock.layerId;
  }, [dock]);
  useEffect(() => {
    if (dock?.dataset === 'volumes') return;
    const staleLayerId = ownVolumesLayerIdRef.current;
    if (!staleLayerId) return;
    ownVolumesLayerIdRef.current = null;
    // Same ask-again contract as `close()` (ruling D36): the slab must not
    // survive being swapped out for the clearance dock.
    store.set(stationClipToBinAtom(staleLayerId), false);
  }, [dock, store]);

  // Opening the dock starts a walk on the analysed alignment: that is what
  // makes the chart cursor, the 3D band and the cross-section one instrument
  // rather than three. `analysis.stationVolumes` and `viewer.alignmentWalk`
  // sit on the same tier (both `{ minProduct: 'PRO' }`), so this is not a way
  // around the walk gate; on a lower tier the dock simply never starts a walk.
  useEffect(() => {
    if (!gate.allowed || !dock || !liveAlignment || !json) return;
    setWalk((w) =>
      w?.key === liveAlignment.key
        ? w
        : {
            key: liveAlignment.key,
            station: json.params.stationFromInternalM,
            step: json.params.stationIntervalM || DEFAULT_WALK_STEP,
            snapKeyPoints: false,
            follow: false,
          },
    );
  }, [gate.allowed, dock, liveAlignment, json, setWalk]);

  // ---- "Clip to station" (spec §11.1) ------------------------------------
  //
  // Cached only: `enabled: false` means this never starts a fetch of its own.
  // If the Section tab has already decoded the planes the query cache still
  // has them (staleTime / gcTime are Infinity), and they bound the slab
  // exactly; otherwise the bins' mean tops do, with a wider pad.
  const { data: offsets } = useStationOffsets(meta, batchId, false);
  // Same cached-only contract: the Section tab's fetch populates this, and the
  // DXF export reads whatever is there. `enabled: false` keeps opening the
  // dock from pulling a second multi-megabyte array nobody has asked to see.
  const { data: sectionPlanes } = useStationSections(meta, batchId, false);

  // ---- Section overlays (section-overlays spec §7) -----------------------
  //
  // The layer's own settings, on the SAME layered merge `useLayerSettings`
  // now applies (`{ ...defaults, ...remote, ...local }`) but with an `enabled`
  // gate that hook has no room for: this dock stays MOUNTED with no dock open,
  // and the hook's per-layer query would fire for a layer id of `'__none'` on
  // every viewer load. Same query key, so the cache is shared both ways with
  // the renderers that already populate it. Precedent and the same reasoning:
  // the settings read in `ClearanceDock`.
  const overlayLayerKey = dock?.layerId ?? '__none';
  const settingsKey = useMemo(() => ['layer-settings', overlayLayerKey], [overlayLayerKey]);
  const queryClient = useQueryClient();
  const [localLayerSettings, setLocalLayerSettings] = useAtom(layerSettingsAtom(overlayLayerKey));
  const remoteLayerSettings = useQuery({
    queryKey: settingsKey,
    queryFn: () => execute(layerSettingsQuery, { id: overlayLayerKey }),
    enabled: !!dock,
  });
  const remoteSettings = remoteLayerSettings.data?.layer?.settings ?? null;
  /**
   * A KEY-BY-KEY merge, not `local ?? remote ?? defaults`.
   *
   * The local atom is `atomWithStorage`, so a browser that opened this project
   * before the feature shipped holds a settings object with no
   * `sectionOverlays` key at all — and under whole-object precedence that
   * object wins OUTRIGHT, leaving `sectionOverlays` `undefined` for ever on
   * that machine, with nothing on screen to explain why the overlays the
   * server has already sampled never appear.
   *
   * Layered defaults → remote → local, so a key the local object is silent
   * about falls through to the value the SERVER holds (and only then to a
   * default). That also matters at the other end of this component: the save
   * sends the whole object back, and filling a gap from a default rather than
   * from the server would quietly reset a setting to factory on save.
   */
  const layerSettings = useMemo(
    () => ({ ...layerDefaultSettings(layer), ...(remoteSettings ?? {}), ...(localLayerSettings ?? {}) }),
    [layer, localLayerSettings, remoteSettings],
  );
  /**
   * The GraphQL row and the client model disagree on nullability (`ref?:` vs
   * `ref:`) and on `__typename`, so the two are mapped HERE and nowhere else:
   * the chart, the popover, `sectionOverlaysHash` and `overlaySeries` all take
   * one shape, and the generated type never reaches them.
   */
  const localOverlays = useMemo(() => toClientOverlays(layerSettings.sectionOverlays), [layerSettings]);
  const remoteOverlays = useMemo(() => toClientOverlays(remoteSettings?.sectionOverlays), [remoteSettings]);
  const overlaysMeta = layerMeta?.sectionOverlays ?? null;
  /**
   * A stale LOCAL list loses to the remote one the SERVER actually sampled.
   *
   * The local atom is per-browser and survives everything; the settings the
   * worker was given are on the row. When they disagree and the stamped
   * `settingsHash` says the SERVER's list is the one that was sampled, the
   * local copy is a leftover — from another browser, another session, or a
   * write that never landed. Believing it turns every drawn overlay
   * "pending", permanently, on a run that is in fact finished.
   *
   * Only ever in that one direction: with no `sectionOverlays` block stamped
   * yet (nothing has ever been sampled) there is nothing to adjudicate.
   *
   * An UNSAVED local edit — the row the user added a moment ago, which is
   * also "local disagrees with the sampled hash" — is not a leftover and must
   * survive. What separates the two is that this component moves the settings
   * QUERY CACHE in step with its own writes (see `onOverlaysChange`), so its
   * own edits never look like a disagreement with the server. Without that
   * this rule reverted every overlay the instant it was added.
   */
  const adoptRemote =
    !!overlaysMeta &&
    sectionOverlaysHash(remoteOverlays) === overlaysMeta.settingsHash &&
    sectionOverlaysHash(localOverlays) !== overlaysMeta.settingsHash;
  const overlaySettings = adoptRemote ? remoteOverlays : localOverlays;
  // …and the leftover is CLEARED, not merely ignored: left in place it would
  // be read back by the next save as the base of the list.
  useEffect(() => {
    if (adoptRemote)
      setLocalLayerSettings({ ...layerSettings, sectionOverlays: remoteSettings?.sectionOverlays ?? [] });
  }, [adoptRemote, layerSettings, remoteSettings, setLocalLayerSettings]);
  // Cached only, like the offsets and the sections above: the SECTION tab's
  // chart owns the fetch. This read is here for the DXF export, which must not
  // pull a multi-megabyte file nobody has asked to see.
  const { data: overlays } = useSectionOverlays(overlaysMeta, batchId, false);
  /**
   * The run's own grid, exactly as `StationSectionChart` derives it — the
   * client twin of `overlaysMatchRun`'s `run` argument. `null` while no
   * byproduct is decoded (this dock never fetches one), in which case there is
   * nothing to disagree with.
   */
  const overlayRunGrid = useMemo(() => {
    const lines = json?.stations;
    if (lines && lines.length > 0 && sectionPlanes)
      return { trueSections: true, nStationLines: sectionPlanes.nStationLines, nOffsets: sectionPlanes.nOffsets };
    if (offsets) return { trueSections: false, nStationLines: offsets.nStations, nOffsets: offsets.nOffsets };
    return null;
  }, [json, sectionPlanes, offsets]);
  /** A file sampled on a grid that is not this run's draws every reference at
   *  the wrong offset (I7). It condemns the whole file, never one plane. */
  const overlaysGridMismatch = !!overlays && !!overlayRunGrid && !overlaysMatchRun(overlays.meta, overlayRunGrid);
  const overlaysUsable = !!overlays && !overlaysGridMismatch;
  const overlayStatesById = useMemo(() => {
    if (overlaysGridMismatch) {
      const failed: Record<string, { state: OverlayState; error?: string | null }> = {};
      for (const s of overlaySettings)
        failed[s.id] = { state: 'failed', error: t('stationVolumes.section.overlays.gridMismatch') };
      return failed;
    }
    return overlayStates(overlaySettings, overlays);
  }, [overlaySettings, overlays, overlaysGridMismatch, t]);
  /**
   * How many CONFIGURED, VISIBLE overlays the printed report would go without
   * (I3). Deliberately NOT `overlayStatesById`: that is derived from a file
   * this dock only reads out of the cache, so on every open where the Section
   * tab has not been visited `overlays` is null and EVERY overlay reads
   * pending — a report dialog warning that nothing has been sampled on a run
   * where everything has.
   *
   * With no file in hand the honest answer comes from the stamped hash, which
   * is on the layer row and needs no bytes: it matches, so the server has
   * sampled this exact list, so nothing is outstanding. Only once the file IS
   * decoded can the count be per-id, and then it is the ids the file does not
   * carry.
   */
  const overlaysPendingCount = useMemo(() => {
    const visible = overlaySettings.filter((s) => !s.hidden);
    if (!overlays) return overlaysMeta?.settingsHash === sectionOverlaysHash(overlaySettings) ? 0 : visible.length;
    // A file that decoded but does not match this run's grid (I7) is not
    // usable — `overlayStatesById` above marks every setting 'failed' and the
    // chart prints nothing, even for an id the file happens to carry. Nothing
    // is outstanding is the one answer that would be wrong here.
    if (overlaysGridMismatch) return visible.length;
    return visible.filter((s) => !overlays.meta.planes.some((p) => p.id === s.id)).length;
  }, [overlaySettings, overlays, overlaysGridMismatch, overlaysMeta]);
  /** The stamp does not describe the list on screen. Answerable from the layer
   *  row alone — no byproduct needed — which is what makes Re-sample reachable
   *  on a dock whose Section tab has never been opened. */
  const overlaysStale =
    overlaySettings.length > 0 && overlaysMeta?.settingsHash !== sectionOverlaysHash(overlaySettings);
  /**
   * Publish the RESOLVED overlay settings for the open layer (spec §5.3).
   *
   * The 3D section overlay draws the same series list this dock hands the
   * chart, but it cannot derive `overlaySettings` for itself: the resolution
   * above is a three-way adjudication between the local atom, the settings
   * query and the stamped hash, and a second copy of it in the scene would
   * drift from this one exactly when they disagree — which is the only time it
   * matters. So the dock, the one component that owns the answer, states it.
   *
   * Cleared when the dock closes, so a scene left mounted after the dock is
   * dismissed never keeps drawing a previous layer's references.
   */
  const setOverlaySettingsPublished = useSetAtom(sectionOverlaySettingsAtom);
  // Keyed on the LAYER ID, never on the `dock` object: `stationVolumeDockAtom`
  // is a derived atom that rebuilds its object on every read, so a `dock`
  // dependency would republish — and re-render the scene — on every hover and
  // every step of the walk.
  const dockLayerId = dock?.layerId ?? null;
  useEffect(() => {
    setOverlaySettingsPublished(dockLayerId ? { layerId: dockLayerId, settings: overlaySettings } : null);
  }, [dockLayerId, overlaySettings, setOverlaySettingsPublished]);
  useEffect(() => () => setOverlaySettingsPublished(null), [setOverlaySettingsPublished]);
  /**
   * The name for each configured overlay, derived from the layer registry
   * only this component holds. The overlays FILE carries the authoritative
   * label; this answers for the ones it does not describe yet — the pending
   * case, which is the one place a reader would otherwise be shown a key.
   */
  const overlayLabels = useMemo(() => {
    const text = overlayLabelText(asOverlayTranslate(t));
    const out: Record<string, string> = {};
    overlaySettings.forEach((s, i) => {
      out[s.id] = overlayLabel(s, layers, text) ?? t('stationVolumes.section.overlays.unnamed', { n: i + 1 });
    });
    return out;
  }, [overlaySettings, layers, t]);
  const [overlaysBusy, setOverlaysBusy] = useState(false);
  const setTrackers = useSetAtom(deviationTrackingAtom);

  /**
   * Save the list, then re-sample it if anything the SERVER cares about moved.
   *
   * Two writes, deliberately in this order and with one exception between
   * them. The settings write is the record of what the user asked for and
   * always happens; the materialise is a JOB and only happens when the
   * SETTINGS HASH changed — `sectionOverlaysHash` excludes `hidden`, so
   * hiding a line is instant and costs nothing, which is the whole point of
   * having a visibility switch rather than a remove button.
   *
   * The settings input is the FULL object with `sectionOverlays` replaced:
   * `updateLayerSettings` replaces the whole settings JSON, so a partial
   * input silently drops every other setting on the layer.
   */
  const onOverlaysChange = async (next: SectionOverlaySetting[]) => {
    if (!dock) return;
    const input = { ...layerSettings, sectionOverlays: next };
    // Optimistic: the popover and the legend are driven by this atom, so the
    // row appears the instant it is added rather than a round trip later.
    const previous = localLayerSettings;
    // The settings query is this component's own idea of "what the server
    // holds", and the stale-local rule above adjudicates the local list
    // against it. The two therefore move TOGETHER: leaving the cache behind
    // makes the row the user has just added look like a leftover from another
    // session, and the rule reverts it before the mutation has even returned.
    //
    // CANCEL FIRST. A refetch of this key already in flight (a window refocus,
    // a mount elsewhere) resolves AFTER the write below and puts the server's
    // pre-edit list back into the cache — where the stale-local rule reads it
    // and reverts the row the user has just added. `cancelQueries` is the
    // standard optimistic-update guard and it is what makes the pair below a
    // write rather than a race.
    await queryClient.cancelQueries({ queryKey: settingsKey });
    const previousRemote = queryClient.getQueryData(settingsKey);
    setLocalLayerSettings(input);
    queryClient.setQueryData(settingsKey, { layer: { id: dock.layerId, settings: input } });
    let saved = false;
    try {
      await execute(updateLayerSettingsMutation, { id: dock.layerId, input });
      saved = true;
      // An empty list needs no sample: with nothing configured the chart draws
      // no reference line and the legend enumerates nothing, whatever the old
      // file still holds.
      if (next.length === 0 || sectionOverlaysHash(next) === sectionOverlaysHash(overlaySettings)) return;
      await materialiseOverlays();
    } catch (err) {
      // A failed SAVE puts the list back: a row left on screen describing
      // something the server never accepted is a lie that survives until the
      // next reload. A failed MATERIALISE keeps it — the settings ARE saved,
      // and the overlay is then legitimately "pending" until it is re-run.
      if (!saved) {
        setLocalLayerSettings(previous);
        // `setQueryData(key, undefined)` is a NO-OP in TanStack Query — the
        // cache entry keeps whatever it held, so a key that had nothing before
        // this save would be left holding the optimistic write for ever.
        // Removing the entry is the only way to restore "nothing cached".
        if (previousRemote === undefined) queryClient.removeQueries({ queryKey: settingsKey, exact: true });
        else queryClient.setQueryData(settingsKey, previousRemote);
      }
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setOverlaysBusy(false);
    }
  };

  /**
   * Queue a SECTION_OVERLAYS job for the layer's CURRENT settings, and track
   * it to completion on one persistent toast.
   *
   * The one materialise path (spec §7.4): `onOverlaysChange` reaches it after
   * a save that moved the settings hash, and the popover's Re-sample button
   * reaches it directly — a stale stamp or a failed overlay is not a settings
   * edit, and without a way in the user's only recourse was to remove a row
   * and add it back.
   *
   * `busy` starts HERE and not at the settings write: it disables the Add
   * buttons, and hiding or removing an overlay — which never reaches this
   * function — must not take them away for the duration of a round trip. It is
   * cleared by the CALLER's `finally`, which also covers the save.
   */
  const materialiseOverlays = async () => {
    if (!dock) return;
    setOverlaysBusy(true);
    const toastId = toast.loading(t('stationVolumes.section.overlays.runningToast'), { duration: Infinity });
    const { materializeSectionOverlays: overlayBatchId } = await execute(materializeSectionOverlaysMutation, {
      layerId: dock.layerId,
    });
    setTrackers((prev) => [
      ...prev,
      {
        toastId,
        batchId: overlayBatchId,
        projectId,
        sourceLayerId: dock.layerId,
        sourceLayerName: layer?.name ?? '',
        // A SECTION_OVERLAYS run mints no layer, so there is nothing for the
        // tracker's layer scan to exclude.
        existingDerivedLayerIds: [],
        kind: 'sectionOverlays' as const,
        // The RUN's batch, not the sample's: it is the one the chart reads
        // the byproduct URLs through, and it has to be invalidated on
        // completion or the open dock keeps showing the previous file.
        runBatchId: batchId,
      },
    ]);
  };

  /** The popover's Re-sample: no settings change, so no save — just the job. */
  const onOverlaysResample = async () => {
    try {
      await materialiseOverlays();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setOverlaysBusy(false);
    }
  };
  const walkActive = !!walk && !!meta && walk.key === meta.alignment.key;
  const clipAllowed = gate.allowed && clipGate.allowed;

  /**
   * "Jump to station" — reported twice. First: moving around the 3D view to
   * find the station you are reading in the table is clumsy. Then, of the
   * first fix: "it should actually jump, not just look".
   *
   * The first fix borrowed `walk.follow`, whose only effect is the walk
   * toolbar's `setCenterTarget`. That handler is a no-op outside third person,
   * and inside it calls `setTarget` — it re-aims the orbit from wherever the
   * camera already is. The camera never moved.
   *
   * So this is its own flag, and jumping means the `lookAt` pose "Look at
   * section" already uses (`lookAtSectionPose`): stand up-station on the
   * tangent and look back at the slab, framed on this run's swath and height.
   * `Viewer` turns that into `moveTo` + `rotateTo` in first person and
   * `setLookAt` otherwise (`lookAtCameraCommand`), so it is a move in every
   * camera mode. Ticking it jumps at once; after that, every walked-station
   * change — a table row, a chart column, `[` / `]` — jumps again.
   */
  const [jumpToStation, setJumpToStation] = useState(false);
  // A surface-difference re-run awaits the run's input before the dialog
  // opens; until then the three re-run triggers are busy and a second click
  // is dropped (the ref, because two clicks can land before a re-render).
  // Declared up here, above the early return, like every hook.
  const [rerunPending, setRerunPending] = useState(false);
  const rerunInFlight = useRef(false);
  const toggleJumpToStation = () => {
    if (!walkActive) return;
    setJumpToStation((on) => !on);
  };
  const clipDims = useMemo(() => (meta ? clipDimsForRun(meta.params, bins, offsets) : null), [meta, bins, offsets]);

  // Framing and alignment are read at EMIT time, not depended on: `clipDims`
  // changes identity whenever the Section tab decodes the offset planes, and a
  // producer re-registering hands out a new Alignment with identical geometry.
  // Depending on either would re-jump a camera the user has since moved away.
  const clipDimsRef = useRef(clipDims);
  useEffect(() => {
    clipDimsRef.current = clipDims;
  }, [clipDims]);
  const walkStation = walk?.station;
  const walkKey = walk?.key;
  useEffect(() => {
    if (!jumpToStation || !walkActive || walkStation === undefined || !walkKey) return;
    const alignment = store.get(alignmentsAtom).get(walkKey);
    const dims = clipDimsRef.current;
    if (!alignment || !dims) return;
    viewerEvents.emit('lookAt', lookAtSectionPose(alignment, walkStation, dims.width, dims.height));
  }, [jumpToStation, walkActive, walkStation, walkKey, store]);

  /**
   * What the walk's clip was before we took it over, and what the viewer's
   * clip box looked like. Refs, not state: they are written from an effect and
   * never rendered, and React Compiler forbids reading a ref during render.
   *
   * `undefined` on the walk ref means "we have not taken it over"; `null` is a
   * real previous value (no slice), which is why the two cannot share a
   * sentinel.
   */
  const clipRestoreRef = useRef<ClipDims | null | undefined>(undefined);
  // GEOMETRY is part of "as we found it", not just the flags. Restoring
  // `enabled`/`clipMode` alone handed a user who already had a clip box of
  // their own back a box that was still enabled and still sitting on the
  // station slab this dock had moved it to — which reads, correctly, as
  // "I turned Clip to station off and the clipping did not turn off".
  const boxRestoreRef = useRef<Pick<
    ClipBoxState,
    'enabled' | 'interactionMode' | 'clipMode' | 'center' | 'halfSize'
  > | null>(null);

  /** Give the walk and the clip box back exactly as we found them. */
  const releaseClip = useCallback(() => {
    if (clipRestoreRef.current !== undefined) {
      const previous = clipRestoreRef.current;
      clipRestoreRef.current = undefined;
      setWalk((w) => (w ? { ...w, clip: previous } : w));
    }
    const box = boxRestoreRef.current;
    if (box) {
      boxRestoreRef.current = null;
      store.set(clipBoxStateAtom(projectId), (prev) => ({ ...prev, ...box }));
    }
  }, [projectId, setWalk, store]);

  useEffect(() => {
    if (!clipToBin || !clipDims || !meta || !walkActive || !liveAlignment || !clipAllowed) {
      // Restore exactly once, and only if we were the ones who changed it.
      releaseClip();
      return;
    }
    const current = store.get(alignmentWalkAtom);
    if (!current) return;
    // The slab belongs to a BIN, not to wherever the slider happens to sit
    // (spec §11.1), so the station is snapped ON THE WAY ON and never again:
    // `clipDims` changes identity whenever the Section tab decodes the offset
    // planes or `layersAtom` re-emits, and re-snapping there would yank a
    // mid-bin walk out from under the user for no reason they could see.
    const takingOver = clipRestoreRef.current === undefined;
    if (takingOver) clipRestoreRef.current = current.clip ?? null;
    const i = binIndexForStation(meta.params, bins.length, current.station);
    const station = takingOver && bins[i] ? binCentreStation(bins[i]) : current.station;
    setWalk({ ...current, station, clip: clipDims });
    // Enable the box in the SAME batch as the walk write, exactly as the walk
    // toolbar's own clip-follow does: that toolbar cancels a `walk.clip` it
    // sees alongside a switched-off box (it reads that as "the user turned the
    // box off elsewhere"), so writing the walk alone would be undone before
    // the next frame. The toolbar's per-frame effect takes it from here as the
    // user steps.
    const box = store.get(clipBoxStateAtom(projectId));
    if (!boxRestoreRef.current) {
      boxRestoreRef.current = {
        enabled: box.enabled,
        interactionMode: box.interactionMode,
        clipMode: box.clipMode,
        center: box.center,
        halfSize: box.halfSize,
      };
    }
    store.set(clipBoxStateAtom(projectId), {
      ...box,
      ...clipBoxAtStation(liveAlignment, station, clipDims),
      enabled: true,
      interactionMode: 'VIEW',
      clipMode: 'CLIP_OUTSIDE',
    });
  }, [clipToBin, clipDims, meta, walkActive, liveAlignment, clipAllowed, bins, projectId, releaseClip, setWalk, store]);

  /**
   * `walk.clip` has two owners now — this toggle and the walk toolbar's own ⬚
   * button, which also nulls it when the clip box is switched off anywhere
   * else. Nothing tells us when the other one acts, and none of the effect
   * above's dependencies move when it does, so the checkbox would sit there
   * ticked over a slab that has stopped following the station.
   *
   * Follow the real state, one way: the slab is gone, so stop claiming it. The
   * walk-clip restore is dropped with it — the other owner has already decided
   * what `walk.clip` should be and writing our copy back would undo them — but
   * `boxRestoreRef` is deliberately kept, so the release below still hands the
   * viewer's own clip box back (the toolbar's ⬚ leaves it enabled and frozen
   * at the last station otherwise).
   */
  const walkClipPresent = !!walk?.clip;
  useEffect(() => {
    if (!clipToBin || !walkActive) return;
    // Never taken over — the toggle has only just been ticked and the effect
    // above has not run yet — so there is nothing to follow.
    if (clipRestoreRef.current === undefined) return;
    // The LIVE value, not `walkClipPresent`: on the commit that ticks the box
    // both effects run against a render where `walk.clip` was still null, and
    // the render-time snapshot would read the take-over as an external clear
    // and undo it. `walkClipPresent` stays in the dependency list as the
    // trigger — it is what changes when another owner acts.
    if (store.get(alignmentWalkAtom)?.clip) return;
    clipRestoreRef.current = undefined;
    setClipToBin(false);
  }, [clipToBin, walkActive, walkClipPresent, setClipToBin, store]);

  // Closing the dock is not "keep my cloud sliced": the toggle is a dock
  // control, so the take-over ends with the dock. `releaseClip` is stable, so
  // this cleanup only ever runs on unmount.
  useEffect(() => releaseClip, [releaseClip]);

  // The Strip tab's two ramp settings, resolved by the SAME hook the tab
  // itself uses so the Export menu's PNG below is the picture the reader is
  // looking at. A hook, so it has to sit above the early return — its call
  // site is `exportStripPng`, 200 lines down.
  const stripSettings = useStripSettings(dock?.layerId ?? null, sectionExpectation, offsets?.dz ?? null);

  // The clearance dock owns the same slot; whichever dataset is open renders
  // and the other returns null (ruling R2).
  const wrongDataset = dock !== null && dock.dataset !== 'volumes';

  if (!dock || !meta || !totals || wrongDataset) return null;

  const p = meta.params;
  /** What the loading / failed badges are about, in the user's words. */
  const badgeName = layer?.name ?? meta.alignment.name;
  // Through `format.ts` like every other figure (D8/A15); the trailing-zero
  // trim keeps `0.25 m` from reading as `0.250 m` in a sentence.
  const resText = formatFixed(p.effectiveResolutionM, 3).replace(/0+$/, '').replace(/\.$/, '');
  const displayStation = (internal: number) =>
    internalToDisplayRaw(internal, meta.alignment.staStart, meta.alignment.equations);

  /**
   * "Design v4 − Existing ground", and on a deviation run the same pair with
   * "(deviation)" after it — the two analyses put different things on either
   * side of that minus, and a reader quoting the figure has to know which.
   *
   * Both names come from the layer's metadata and either can be missing (an
   * older run, a deleted source layer): with one of them blank the template
   * rendered a dangling minus, and with both blank a lone " − " floating in
   * the header. Say what is known, or nothing.
   */
  const topName = (meta.topLayerName ?? sd?.topLayerName)?.trim() ?? '';
  const bottomName = (meta.bottomLayerName ?? sd?.bottomLayerName)?.trim() ?? '';
  const analysisText =
    topName && bottomName
      ? t(kind === 'deviation' ? 'stationVolumes.panel.analysisDeviation' : 'stationVolumes.panel.analysis', {
          top: topName,
          bottom: bottomName,
        })
      : topName || bottomName;

  const rangeText = bins.length
    ? formatStationRange({
        staStartDisplayM: bins[0].staStartDisplayM,
        staEndDisplayM: bins[bins.length - 1].staEndDisplayM,
      })
    : formatStationRange({
        staStartDisplayM: displayStation(p.stationFromInternalM),
        staEndDisplayM: displayStation(p.stationToInternalM),
      });

  // BOTH ends are clamped: a brush left behind by a longer run (a refetch
  // with fewer bins) would otherwise index past the array and take the dock
  // down with it.
  const selLoBin = sel ? Math.max(0, Math.min(bins.length - 1, Math.min(sel.fromBin, sel.toBin))) : 0;
  const selHiBin = sel ? Math.max(0, Math.min(bins.length - 1, Math.max(sel.fromBin, sel.toBin))) : 0;
  // Only read while a brush is active (the tab bar's selection chip).
  const selectionText =
    sel && bins.length
      ? formatStationRange({
          staStartDisplayM: bins[selLoBin].staStartDisplayM,
          staEndDisplayM: bins[selHiBin].staEndDisplayM,
        })
      : '';

  // Detailed Station volume readouts keep the same three-decimal precision as
  // stations and elevations throughout the dock (the existing G9 formatting,
  // unchanged by the round-3 overhaul — only the unit moved onto every
  // figure, so the table, chart and header still print one number alike).
  const [cutText, fillText, netText] = formatM3Row(
    [totals.cut, totals.fill, totals.net],
    STATION_VOLUME_DISPLAY_DECIMALS,
  );
  // Coverage follows the same scope as the totals beside it: the brushed range
  // when there is a brush, the whole corridor otherwise. Whole percent
  // everywhere in this feature, and the unit is stated once — in the KPI's
  // unit slot, in the table's column header, after the tooltip's figure.
  const coveragePct = totals.coveragePct;
  const coverageText = coveragePct == null ? '—' : formatPct(coveragePct);
  // Identity of the scope these figures describe, not of the figures
  // themselves, and not of the SELECTION: keying on the bin indices remounted
  // the value span for every bin the brush crossed, so dragging strobed the
  // hero totals. The fade fires when the reader's question changes — corridor
  // to selection and back — and never while they are still asking it.
  const totalsScope = sel ? 'selection' : 'corridor';

  /** The footer says the coverage in a sentence, so the unit rides with it. */
  const coverageFooterText = coveragePct == null ? '—' : `${coverageText} %`;

  /** Which layer of the stack the three views are showing. `undefined` for a
   *  single-pair run, so the attribute is simply absent there. */
  const stackLayerAttr = stackMeta ? String(activeLayerIndex) : undefined;
  /**
   * Why this stack cannot print a per-station PDF, or `null` when it can (and
   * always `null` for a single-pair run, which reaches the item on `json`
   * alone). The dock holds all three inputs the gate needs: the metadata
   * block, the fetched stack document and the active chip.
   */
  const stackBlock = perStationPdfStackBlock(stackMeta, stackDoc ?? null, activeLayerIndex);
  /** The selected layer of the stack has not been built yet — one of the two
   *  levels either side of it has no as-built survey, so there is nothing to
   *  measure. A state, never a zero (global constraint §4.3). */
  const stackPending = !!stack && stack.layer?.state === 'PENDING';

  const chartTooNarrow = width < DOCK_VERY_NARROW_PX;
  const hasData = !!json && bins.some((b) => b.cellCount > 0);
  const isEmpty = !!json && !hasData;
  const schemaTooNew = error?.message === 'SCHEMA_TOO_NEW';

  const initialStationParams: StationVolumesInitialParams = {
    alignmentKey: meta.alignment.key,
    stationInterval: p.stationIntervalM,
    swathLeft: p.swathLeftM,
    swathRight: p.swathRightM,
    offsetBin: p.offsetBinM,
    minCoveragePct: p.minCoveragePct,
    // The dialog turns these INTERNAL stations into display text once, out of
    // `defaultValues`, against the alignment registry as it stands when it
    // mounts. With the alignment missing there is nothing to convert them
    // against and nothing re-syncs them later, so sending them would pre-fill
    // a range the dialog silently drops. Omit them and let the dialog's
    // full-length placeholders say what will actually be measured.
    ...(liveAlignment ? { stationFrom: p.stationFromInternalM, stationTo: p.stationToInternalM } : {}),
    // The check travels with the swath it was measured over — a re-run that
    // silently dropped it would look like the user turned it off.
    ...(p.crossfall ? { crossfall: p.crossfall } : {}),
  };

  /** The deviation re-run needs the point cloud the run was made from; it can
   *  have been deleted since, in which case there is no dialog to open. */
  const deviationSource = kind === 'deviation' ? layers.find((l) => l.id === layerMeta?.derivedFrom) : undefined;
  const canRerun = kind === 'deviation' ? !!deviationSource : true;

  /**
   * The swath the reference surface actually needs, from the reach the
   * worker measured — the "narrow the swath to the road" re-run. `null` on a
   * run older than the extents, or one that covered nothing. Corridor-wide by
   * nature (the extents are), whatever is brushed.
   */
  const fittedSwath = fittedSwathFromExtents(meta.summary.referenceExtentLeftM, meta.summary.referenceExtentRightM);
  const referenceBasis = p.coverageBasis === 'REFERENCE_SURFACE';
  /** The swath is wider than the surface being measured: information with a
   *  one-click fix, never a warning — coverage is measured against the
   *  surface, so nothing about the figures is in doubt. Follows the brushed
   *  range like every figure beside it. */
  const swathUnderused = referenceBasis && totals.swathUsedPct != null && totals.swathUsedPct < SWATH_UNDERUSED_PCT;

  const openRerun = async (swath?: { left: number; right: number }) => {
    if (rerunInFlight.current) return;
    rerunInFlight.current = true;
    setRerunPending(true);
    try {
      await openRerunNow(swath);
    } finally {
      rerunInFlight.current = false;
      setRerunPending(false);
    }
  };
  const openRerunNow = async (swath?: { left: number; right: number }) => {
    // Re-running is running: the dock's own gate says nothing about whether
    // this user may start a comparison, and without this check the dock is a
    // way around the paywall. The gate is the Compare surfaces descriptor's,
    // the same one the hub's re-run checks.
    const gateKey = analysisGate('compare');
    const runGate = gateKey ? featureCheck(gateKey) : null;
    if (runGate && !runGate.allowed) {
      runGate.promptUpgrade();
      return;
    }
    // The whole point of "re-run with new parameters" is that only the
    // parameters the user changes differ from the run they are looking at,
    // so the run goes through the hub's own mapping (`buildRerunPlan`, same
    // defaults) and only the station section is the dock's: it knows the
    // live alignment, the crossfall check and the fitted swath.
    let plan: RerunPlan | null;
    if (kind === 'deviation') {
      if (!deviationSource) return;
      // Everything a deviation re-run restores is echoed onto the layer. The
      // run measured stations (this dock is showing them), and an
      // expectation when the corridor verdict has one: both move onto cut &
      // fill.
      plan = buildRerunPlan(
        'compare',
        {
          sourceLayerId: deviationSource.id,
          referenceLayerId: layerMeta?.referenceLayerId,
          params: layerMeta?.deviationParams,
          stationVolumes: { ...p, alignmentKey: meta.alignment.key },
          expectation: corridorExpectation,
        },
        layers,
        ProcessJobType.Deviation,
      );
    } else {
      // A surface difference's layer carries no surface picks, sampling or
      // name: read the run's own input, as the hub does.
      const jobId = layerMeta?.sourceProcessJobId;
      const input = jobId ? await fetchAnalysisRunInput(queryClient, jobId) : null;
      if (input == null) {
        toast.error(t('analysisLauncher.runs.inputFailed'));
        return;
      }
      plan = buildRerunPlan('compare', input, layers, ProcessJobType.SurfaceDifference);
    }
    if (plan?.id !== 'compare') return;
    // The same hint the hub gives: the dialog opens with the lost side empty.
    if (plan.missingInputs) toast.info(t('analysisLauncher.runs.inputsMissing'));
    const stationParams: StationVolumesInitialParams = swath
      ? { ...initialStationParams, swathLeft: swath.left, swathRight: swath.right }
      : initialStationParams;
    // The one re-run opener (`rerunOpeners`), shared with the Analyses hub —
    // same candidate pool, same shell class.
    openCompareSurfacesRerun({ openDialog, projectId, layers }, { ...plan, initialStationParams: stationParams });
  };

  /** The re-run button, or the sentence that explains its absence. A control
   *  that opens nothing reads as a bug; the reason does not. Neither on a
   *  shared link, which can never re-run. */
  const rerunControl = isSharedLink ? null : canRerun ? (
    <Button
      data-testid='sv-rerun'
      type='button'
      size='sm'
      variant='outline'
      disabled={rerunPending}
      aria-busy={rerunPending || undefined}
      onClick={() => void openRerun()}>
      {rerunPending && <Loader2 aria-hidden className='size-3.5 animate-spin' />}
      {t('stationVolumes.empty.rerun')}
    </Button>
  ) : (
    <p data-testid='sv-rerun-unavailable' className='text-slate-500 dark:text-stone-400'>
      {t('stationVolumes.empty.rerunUnavailable')}
    </p>
  );

  // The in-app CSV is the worker's byproduct file, byte for byte (T12.1), so
  // the two never disagree about what the run measured. The surface names are
  // ours to supply: they live on the layer's metadata, not in the bins file.
  const exportOptions = (rangeOnly: boolean): StationExportOptions => ({
    topLayerName: topName || undefined,
    bottomLayerName: bottomName || undefined,
    // The download and the worker's byproduct are the same document, so the
    // `# Analysis:` line has to name the same analysis in both — which is why
    // this reads the bins file's OWN `analysisKind`, the field the worker's
    // CSV writer prints from. The layer-metadata `kind` is the fallback for a
    // bins file written before the field existed.
    analysisKind: json?.analysisKind ?? (kind === 'deviation' ? 'deviation' : 'surface difference'),
    // The stale-alignment chip in the header does not travel with the file
    // (M9/D58). The CSV carries the run's `# Alignment hash:` and nothing to
    // compare it against, so when the live alignment has moved on the export
    // says so in words — one line, appended after the worker's own block.
    alignmentStale: !hashOk,
    ...(rangeOnly && sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : {}),
  });

  const exportCsv = (rangeOnly: boolean) => {
    if (!json) return;
    const o = exportOptions(rangeOnly);
    // U+FEFF: Excel on Windows reads a BOM-less UTF-8 CSV as mojibake, and
    // this header block is full of em dashes and a U+2212 minus. The worker
    // writes its own copy with `utf-8-sig` for the same reason; the BOM is
    // added at download time, so the string itself stays the shared contract.
    downloadFile(`\ufeff${stationVolumesToCsv(json, o)}`, stationExportFilename(json, o), 'text/csv;charset=utf-8');
  };

  /** The Strip map tab's picture, from the Export menu (strip-map spec §3.5).
   *  A plain function, not a `useCallback`: everything from here down sits
   *  below the dock's early return and may not call a hook. `saveStripPng` is
   *  the tab's own helper, so both routes compose, name and — when the canvas
   *  refuses to encode — REPORT the failure identically. */
  const exportStripPng = async () => {
    if (!json || !offsets) return;
    const { toleranceM, spanM } = stripSettings;
    await saveStripPng({ json, offsets, kind, toleranceM, spanM, t });
  };

  const copyTsv = () => {
    if (!json) return;
    const tsv = stationVolumesToTsv(json, exportOptions(true));
    // Counted off the bytes that are actually going to the clipboard (one
    // column header plus a line per bin), not off the selection's indices: a
    // stale brush that outruns the bin array would otherwise overstate what
    // the reader just copied.
    const copiedBins = tsv.split('\n').length - 1;
    // Clipboard access can be refused (permissions, an insecure origin),
    // absent entirely, or throw synchronously — say so rather than leaving
    // the user to discover an empty paste.
    try {
      const written = navigator.clipboard?.writeText(tsv);
      if (!written) {
        toast.error(t('stationVolumes.export.copyFailed'));
        return;
      }
      written
        // Plural-correct and locale-grouped (code review #4): "1 station
        // interval", "1,204 station intervals".
        .then(() =>
          toast.success(
            t('stationVolumes.export.copied', { count: copiedBins, formatted: copiedBins.toLocaleString() }),
          ),
        )
        .catch(() => toast.error(t('stationVolumes.export.copyFailed')));
    } catch {
      toast.error(t('stationVolumes.export.copyFailed'));
    }
  };

  /**
   * A stack has station rows to write at all — i.e. the run was given an
   * alignment, so `analyse_stack` produced a `stations` array for at least one
   * layer. `worker.py` refuses to write `stack_stations.csv` in that state for
   * the same reason: a header with no rows reads as "measured nothing" rather
   * than "never asked for".
   */
  const stackHasStationRows = !!stackDoc?.layers?.some((l) => (l.stations?.length ?? 0) > 0);

  /**
   * The stack's OWN two CSVs — the byte-identity twins of
   * `stack_analysis.py::stack_layers_csv` / `stack_stations_csv`
   * (`lib/layerStack/csv.ts`), pinned against the committed Python fixtures.
   *
   * A stack must NOT go out through `stationVolumesToCsv`. That writer appends
   * its nine expectation columns only when `json.expectation` is set, and the
   * synthesised stack json never sets it (a stack judges thickness per LEVEL,
   * against a different pair of tolerances per layer, so there is no single
   * run-wide expectation block to put there). The result was a station-volumes
   * file with no thickness and no verdict in it — a compliance export with the
   * compliance dropped — which is the one thing a surveyor clicks this menu
   * for.
   *
   * Two documents, not one: the layer CSV is one row per LAYER against the
   * UNION GRID, the station CSV one row per (bin × layer) against the
   * CORRIDOR. Their identically-named `not_measured_pct` columns have
   * different denominators and the two files deliberately do not join.
   */
  const exportStackCsv = (which: 'layers' | 'stations') => {
    if (!stackDoc) return;
    const generatedAtIso = new Date().toISOString();
    const text =
      which === 'layers' ? stackLayersCsv(stackDoc, generatedAtIso) : stackStationsCsv(stackDoc, generatedAtIso);
    const name =
      (which === 'layers' ? stackMeta?.byproducts?.layersCsv : stackMeta?.byproducts?.stationsCsv) ??
      (which === 'layers' ? STACK_LAYERS_CSV_FILENAME : STACK_STATIONS_CSV_FILENAME);
    // U+FEFF for the same reason the station-volumes download carries one:
    // Excel on Windows reads a BOM-less UTF-8 CSV as mojibake, and both `#`
    // blocks are full of em dashes and user-typed level names. Added at
    // download time only — the STRING is the contract the fixtures pin, and
    // it stays byte-identical to the worker's file.
    downloadFile(`\ufeff${text}`, name, 'text/csv;charset=utf-8');
  };

  /**
   * The rows the DXF draws, and whether they are true station-line cuts.
   *
   * True sections when the run has them, the bin-mean planes otherwise — an
   * older analysis still exports, and the sheet's title block says which it
   * got rather than quietly presenting one as the other.
   *
   * Built in the click handler, not a `useMemo`: this sits below the dock's
   * early return, and mapping every bin on every render to answer a question
   * only the Export menu asks would be work nobody waits for.
   */
  const buildDxfSections = (): { rows: SectionInput[]; intervalMeans: boolean } => {
    if (!json) return { rows: [], intervalMeans: false };
    const lines = json.stations;
    if (lines && sectionPlanes) {
      // The chip's pair is `zTop`/`zBottom` — the surfaces the cut/fill bands
      // and the printed areas belong to. Every OTHER drawn plane rides along
      // as a series and lands on its own layer, so a stack exports the whole
      // stack rather than the two levels that happen to be selected.
      // …and the REFERENCE overlays ride along too, each on its own
      // `SECTION-<name>` layer via `dxfSeriesForLine`. Exactly the planes the
      // SCREEN draws (`overlaysUsable` — the same id match and the same grid
      // check the chart applies), so the sheet and the section agree; a file
      // sampled on another run's grid is declined whole.
      const series = [
        ...runSeries(sectionPlanes, null),
        ...(overlaysUsable ? overlaySeries(overlays, overlaySettings, palette.series) : []),
      ];
      return {
        intervalMeans: false,
        rows: lines.map((l, k) => {
          const planes = dxfSeriesForLine(
            seriesRowsAt(series, k),
            stack?.planePair ?? null,
            t('stationVolumes.section.legendDesign'),
          );
          return {
            staInternalM: l.staInternalM,
            staDisplayM: l.staDisplayM,
            zTop: planes.zTop,
            zBottom: planes.zBottom,
            cutAreaM2: l.cutAreaM2,
            fillAreaM2: l.fillAreaM2,
            series: planes.series,
          };
        }),
      };
    }
    if (offsets) {
      return {
        intervalMeans: true,
        rows: bins.map((b) => {
          const row = sectionRowAt(offsets, b.i);
          return {
            // A bin-mean column stands for the whole interval, so it is drawn
            // at the interval's MIDPOINT rather than at either edge — the one
            // station it is actually an estimate of.
            staInternalM: (b.staStartInternalM + b.staEndInternalM) / 2,
            staDisplayM: (b.staStartDisplayM + b.staEndDisplayM) / 2,
            zTop: row.zTop,
            zBottom: row.zBottom,
            cutAreaM2: b.cutAreaM2,
            fillAreaM2: b.fillAreaM2,
          };
        }),
      };
    }
    return { rows: [], intervalMeans: false };
  };

  // Cheap enough for render: whether there is anything to draw, without
  // actually building the rows.
  const canExportDxf = !!json && ((!!json.stations && !!sectionPlanes) || !!offsets);
  // The areas CSV needs only the JSON, not the planes: it is numbers, not a
  // drawing, so it is available the moment the bins file has loaded.
  const hasStationLines = !!json?.stations && json.stations.length > 0;

  /**
   * The per-chainage areas as their own CSV — one row per station LINE, the
   * same document the worker registers as `station_sections.csv`. It answers
   * the question the interval CSV cannot: what is the area between the two
   * surfaces AT this chainage.
   */
  const exportSectionsCsv = (rangeOnly: boolean) => {
    if (!json) return;
    const o = exportOptions(rangeOnly);
    const csv = stationSectionsToCsv(json, o);
    if (!csv) {
      toast.error(t('stationVolumes.export.sectionsCsvUnavailable'));
      return;
    }
    // The BOM is added at download time, as it is for the interval CSV: it
    // belongs to the FILE, not to the string the contract compares.
    downloadFile(`\ufeff${csv}`, stationSectionsFilename(json, o), 'text/csv;charset=utf-8');
  };

  const openSectionDxf = () => {
    const built = buildDxfSections();
    if (!json || built.rows.length === 0) {
      toast.error(t('stationVolumes.export.sectionsDxfUnavailable'));
      return;
    }
    openDialog(
      <StationSectionDxfDialog
        json={json}
        sections={built.rows}
        intervalMeans={built.intervalMeans}
        bins={bins}
        stations={json.stations ?? null}
        alignment={liveAlignment}
        selection={sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : null}
        alignmentName={meta.alignment.name}
      />,
    );
  };

  const openPerStationPdf = () => {
    // A stack whose run is missing any of the three things the server resolves
    // the document from cannot print, and the item above is disabled for it.
    // This is the second half of the same gate, for the keyboard and for any
    // future caller — Radix `disabled` is an attribute, not an HTML one.
    if (stackBlock) return;
    // The same scope rule as the selection CSV: the brush if there is one, the
    // whole corridor otherwise. The page count is computed here so the confirm
    // dialog can quote it and the cap can be refused in words rather than as a
    // failed batch five minutes later.
    const scope = perStationPdfScope(bins, sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : null);
    if (scope.pageCount === 0) {
      toast.error(t('stationVolumes.export.perStationPdfEmpty'));
      return;
    }
    if (scope.overCap) {
      toast.error(t('stationVolumes.export.perStationPdfTooMany', { count: scope.pageCount, max: MAX_STATION_PAGES }));
      return;
    }
    onOpenPdfReport?.({
      projectId,
      layerId: dock.layerId,
      layerName: layer?.name ?? meta.alignment.name,
      mode: 'perStation',
      // WHICH layer of the stack. The active chip — the same layer whose bins
      // `bins` above already holds, so the page count quoted in the dialog and
      // the pages the server prints are counted over the same array. Omitted
      // for a single-pair run, which has no layers to choose between.
      ...(stackMeta ? { stackLayerIndex: activeLayerIndex } : {}),
      binCount: scope.pageCount,
      renderCount: scope.renderCount,
      stationFrom: scope.stationFrom,
      stationTo: scope.stationTo,
      // The WINDOW, not the pages: a blank bin at either end of the brush gets
      // no page but is inside the range the server narrows to and the cover
      // note states, so the sentence in the dialog has to say the same span.
      stationRangeLabel:
        scope.displayFrom !== null && scope.displayTo !== null
          ? formatStationRange({ staStartDisplayM: scope.displayFrom, staEndDisplayM: scope.displayTo })
          : null,
      rendersAvailable: stationRendersAvailable(meta),
      renderRange,
      renderColormap,
      // How many configured section overlays (Task 14) have not finished
      // sampling yet — the dialog says so before this job is queued and
      // paid for, since every page it prints comes from the overlay bytes
      // as they stand right now.
      overlaysPendingCount,
    });
  };

  /**
   * The LAYER-STACK COMPLIANCE REPORT (spec §7) — the document with the
   * structure summary, the per-layer compliance table, the roll-up verdict
   * and the level provenance. This is the ONLY caller: everything behind it
   * (`preprocess.ts`'s LAYER_STACK arm, `html.ts`'s stack block, the
   * "Layer Thickness Report" cover) was complete and reachable by nothing,
   * so a stack user's only PDF was the per-station one — titled "Station
   * Volumes Report", headed "Surface difference", with no verdict on it.
   *
   * Deliberately a much WEAKER gate than the per-station item above. The
   * server's LAYER_STACK arm needs the result layer's `layerStack` metadata
   * and `stack.json`, and nothing else: a run with no alignment prints the
   * compliance half and says the stations were omitted, and a PENDING layer
   * gets a section that says "not built yet". So the only things that stop
   * this are the two the server also refuses on — no stack metadata, no
   * document — plus "the document has not arrived yet", which is what keeps
   * the counts in the dialog's scope line honest rather than zero.
   */
  const openLayerStackPdf = () => {
    if (!stackMeta || !stackDoc) return;
    const scope = layerStackReportScope(stackDoc, bins, sel ? { fromBin: sel.fromBin, toBin: sel.toBin } : null);
    onOpenPdfReport?.({
      projectId,
      layerId: dock.layerId,
      layerName: layer?.name ?? meta.alignment.name,
      mode: 'layerStack',
      // N−1 layers and every layer's bins summed: ruling R16 spends ONE page
      // budget across the whole stack, so a per-layer count would make the
      // server's refusal look arbitrary.
      layerCount: scope.layerCount,
      binCount: scope.binCount,
      // The stack report draws no point-cloud renders; the checkbox is hidden
      // rather than offered and refused.
      renderCount: 0,
      rendersAvailable: false,
      stationFrom: scope.stationFrom,
      stationTo: scope.stationTo,
      stationRangeLabel:
        scope.displayFrom !== null && scope.displayTo !== null
          ? formatStationRange({ staStartDisplayM: scope.displayFrom, staEndDisplayM: scope.displayTo })
          : null,
    });
  };

  // ---- Header: identity, warnings, figures --------------------------------

  /** Scoped like every figure beside it: a brush narrows the question, so the
   *  figures, the coverage and this sentence all narrow with it. */
  const selActive = !!sel && bins.length > 0;

  /**
   * The footer's two sentences, now the first row of ⓘ Details (spec §B2).
   * One scope per sentence: mixing a selection's bin count with the
   * corridor's length and coverage is how a reader mis-quotes. `selActive`,
   * not `sel`: a brush left behind by a longer run survives the refetch, and
   * with no bins to scope it to `totals` falls back to the corridor summary —
   * a sentence about a selection that is not on screen.
   *
   * Plural on the count the NOUN agrees with (code review #4): the corridor's
   * interval count, and the selection's TOTAL ("1 of 3 station intervals",
   * "1 of 1 station interval"). Counts and the length are locale-grouped.
   */
  const corridorTotal = bins.length || meta.summary.binCount;
  const scopeSentence = selActive
    ? t('stationVolumes.panel.footerSelection', {
        count: corridorTotal,
        formatted: totals.binCount.toLocaleString(),
        total: corridorTotal.toLocaleString(),
        length: Math.round(totals.lengthM).toLocaleString(),
        coverage: coverageFooterText,
      })
    : t('stationVolumes.panel.footerCorridor', {
        count: totals.binCount,
        formatted: totals.binCount.toLocaleString(),
        interval: p.stationIntervalM,
        length: Math.round(totals.lengthM).toLocaleString(),
        coverage: coverageFooterText,
      });

  const crossfall = meta.summary.crossfall;
  const swathHelp = swathUnderused
    ? t(kind === 'deviation' ? 'stationVolumes.panel.swathUsedHelpDeviation' : 'stationVolumes.panel.swathUsedHelp', {
        pct: formatPct(totals.swathUsedPct!),
        width: formatFixed(p.swathLeftM + p.swathRightM, 1),
        left: meta.summary.referenceExtentLeftM != null ? formatFixed(meta.summary.referenceExtentLeftM, 1) : '?',
        right: meta.summary.referenceExtentRightM != null ? formatFixed(meta.summary.referenceExtentRightM, 1) : '?',
      })
    : '';
  // Every one of these is a WARNING, never folded away: "the alignment has
  // changed since this run" is not a parameter, it is a reason to distrust
  // every number beside it. Compact chips after the identity; the long
  // explanation is each chip's tooltip. Most serious first — the header
  // collapses them into one "⚠ N" chip on a narrow dock, and its list reads
  // in this order: a stale or missing alignment undermines everything else.
  const warningChips = [
    !hashOk && (
      <DockChip
        key='stale'
        tone='warn'
        testId='sv-stale-alignment'
        title={t('stationVolumes.panel.alignmentChangedHelp')}
      >
        {t('stationVolumes.panel.alignmentChanged')}
      </DockChip>
    ),
    !liveAlignment && (
      <DockChip key='missing' testId='sv-alignment-missing' title={t('stationVolumes.panel.alignmentMissingHelp')}>
        {t('stationVolumes.panel.alignmentMissing')}
      </DockChip>
    ),
    meta.alignment.curvatureWarning && (
      <DockChip key='curvature' tone='warn' testId='sv-curvature' title={t('stationVolumes.panel.curvatureHelp')}>
        {t('stationVolumes.panel.curvature', {
          radius: meta.alignment.minRadiusM != null ? formatFixed(meta.alignment.minRadiusM, 0) : '?',
        })}
      </DockChip>
    ),
    // `totals`, not `meta.summary`: the Cut/Fill/Net and the coverage KPI
    // beside this chip are the BRUSHED range, and one scope per sentence is the
    // rule. `totals` is the corridor when nothing is brushed (and falls back to
    // the summary before the bins land), so the chip follows its figures.
    totals.lowCoverageCount > 0 && (
      <DockChip
        key='low-coverage'
        tone='warn'
        testId='sv-low-coverage'
        // Compact on the chip ("Low coverage 3/60") so a one-row header keeps
        // room for the run's name; the sentence and its explanation are the
        // tooltip. The basis decides what "coverage" was measured against —
        // the reference surface's footprint on a current run, the whole swath
        // on an older one — and the explanation has to say which.
        title={`${t('stationVolumes.panel.lowCoverage', {
          count: totals.lowCoverageCount,
          total: totals.binCount,
        })}. ${t(
          referenceBasis
            ? kind === 'deviation'
              ? 'stationVolumes.panel.lowCoverageHelpRefDeviation'
              : 'stationVolumes.panel.lowCoverageHelpRef'
            : kind === 'deviation'
              ? 'stationVolumes.panel.lowCoverageHelpDeviation'
              : 'stationVolumes.panel.lowCoverageHelp',
          { min: p.minCoveragePct },
        )}`}
      >
        <span className='sv-num'>
          {t('stationVolumes.panel.lowCoverageShort', { count: totals.lowCoverageCount, total: totals.binCount })}
        </span>
      </DockChip>
    ),
    // Crossfall, from `summary.crossfall` — its OWN chip, never folded into
    // the run's roll-up verdict (crossfall spec A-5: advisory in v1).
    // Corridor-wide by nature: there is no per-bin figure to re-total over a
    // brush. The tone is keyed off the COUNTS (MARGINAL included — 40 near
    // misses and no failure is not a quiet run), never off
    // `worstAbsDeltaPct`, which reads 4.3e-13 on a perfect corridor.
    crossfall && (
      <DockChip
        key='crossfall'
        tone={crossfall.fail + crossfall.marginal > 0 ? 'warn' : undefined}
        testId='sv-crossfall-chip'
        title={t('stationVolumes.panel.crossfallChipHelp', {
          tol: crossfall.tolerancePct,
          bands: [
            p.crossfall?.left &&
              t('stationVolumes.panel.crossfallChipHelpBands', {
                side: t('stationVolumes.dialog.crossfallLeftSide'),
                from: formatFixed(p.crossfall.left.fromM, 2),
                to: formatFixed(p.crossfall.left.toM, 2),
              }),
            p.crossfall?.right &&
              t('stationVolumes.panel.crossfallChipHelpBands', {
                side: t('stationVolumes.dialog.crossfallRightSide'),
                from: formatFixed(p.crossfall.right.fromM, 2),
                to: formatFixed(p.crossfall.right.toM, 2),
              }),
          ]
            .filter(Boolean)
            .join(' · '),
        })}
      >
        {/* The marginal count only earns a slot when there IS one. */}
        {t(
          crossfall.marginal > 0 ? 'stationVolumes.panel.crossfallChipMarginal' : 'stationVolumes.panel.crossfallChip',
          {
            fail: crossfall.fail,
            marginal: crossfall.marginal,
            inconclusive: crossfall.inconclusive,
          },
        )}
      </DockChip>
    ),
    swathUnderused && (
      <span
        key='swath'
        className='flex items-center gap-1.5'
        data-testid='sv-swath-used'
        // The group's full text, for the collapsed warnings list (which prints
        // each item's `title`); the chip inside carries the same tooltip.
        title={swathHelp}
      >
        <DockChip title={swathHelp}>
          {t('stationVolumes.panel.swathUsed', { pct: formatPct(totals.swathUsedPct!) })}
        </DockChip>
        {/* The fix beside the fact: the same re-run the empty state offers,
            pre-filled with the swath the surface needs. A stack has no single
            re-run dialog, so it gets the fact only. */}
        {fittedSwath && canRerun && !stackMeta && !isSharedLink && (
          <button
            type='button'
            data-testid='sv-rerun-fitted'
            disabled={rerunPending}
            aria-busy={rerunPending || undefined}
            className='sv-chip inline-flex cursor-pointer items-center gap-1 text-[10px] whitespace-nowrap text-sky-700 hover:text-sky-900 focus-visible:ring-2 focus-visible:ring-sky-500 focus-visible:outline-none disabled:cursor-wait disabled:opacity-70 dark:text-sky-300 dark:hover:text-white'
            onClick={() => void openRerun(fittedSwath)}
          >
            {rerunPending && <Loader2 aria-hidden className='size-3 animate-spin' />}
            {t('stationVolumes.panel.rerunFitted', {
              left: formatFixed(fittedSwath.left, 1),
              right: formatFixed(fittedSwath.right, 1),
            })}
          </button>
        )}
      </span>
    ),
  ].filter(Boolean);

  /**
   * The header's figures, in the spec's order (§B4): Verdict, Cut, Fill, Net,
   * Coverage. Coverage then Fill give way first on a narrow dock and move into
   * ⓘ Details. Every m³ figure carries its unit — G9's magnitude ladder
   * (`formatM3Row` with no forced precision) decides the decimals for the row
   * as a whole, so the three read in one precision.
   */
  const kpiItems: DockKpiItem[] = [
    // Scope-aware like the totals it sits beside: the verdict was computed
    // over the WHOLE corridor, so it is suppressed — never recomputed — while
    // a brush narrows the question to a sub-range. No "Verdict" label: the
    // chip is its own label.
    ...(corridorExpectation && !sel
      ? [
          {
            key: 'verdict',
            priority: 0 as const,
            node: (
              <div className='flex items-center' data-testid='sv-corridor-verdict'>
                <VerdictChip verdict={corridorExpectation.verdict} criteria={corridorExpectation.criteria} />
              </div>
            ),
          },
        ]
      : []),
    {
      key: 'cut',
      priority: 0,
      node: (
        <DockKpi
          scopeKey={totalsScope}
          label={t('stationVolumes.panel.cut')}
          value={cutText}
          unit={t('stationVolumes.panel.units')}
          unitTestId='sv-cut-unit'
          color='var(--sv-cut)'
          testId='sv-total-cut'
        />
      ),
    },
    {
      key: 'fill',
      priority: 2,
      node: (
        <DockKpi
          scopeKey={totalsScope}
          label={t('stationVolumes.panel.fill')}
          value={fillText}
          unit={t('stationVolumes.panel.units')}
          unitTestId='sv-fill-unit'
          color='var(--sv-fill)'
          testId='sv-total-fill'
        />
      ),
    },
    {
      key: 'net',
      priority: 0,
      node: (
        <DockKpi
          scopeKey={totalsScope}
          label={t('stationVolumes.panel.net')}
          value={netText}
          unit={t('stationVolumes.panel.units')}
          testId='sv-total-net'
        />
      ),
    },
    {
      key: 'coverage',
      priority: 1,
      node: (
        <DockKpi
          scopeKey={totalsScope}
          label={t('stationVolumes.panel.coverage')}
          title={t(
            kind === 'deviation' ? 'stationVolumes.panel.coverageHelpDeviation' : 'stationVolumes.panel.coverageHelp',
          )}
          value={coverageText}
          unit={coveragePct == null ? undefined : t('stationVolumes.table.unitsPct')}
          unitTestId='sv-coverage-unit'
          testId='sv-coverage'
        />
      ),
    },
  ];

  /**
   * ⓘ Details: the reference material the header chips and the footer used to
   * print (finding M9 still holds — a figure whose settings you cannot see
   * cannot be checked — so it is one click away, not gone). The old test ids
   * ride on the rows.
   */
  const detailsSections: DockDetailsSection[] = [
    {
      key: 'run',
      title: t('stationVolumes.panel.detailsRun'),
      testId: 'sv-details-run',
      rows: [
        { key: 'scope', value: scopeSentence, testId: 'sv-footer-scope' },
        ...(analysisText
          ? [{ key: 'surfaces', label: t('stationVolumes.panel.detailsSurfaces'), value: analysisText }]
          : []),
        { key: 'range', label: t('stationVolumes.panel.rangeLabel'), value: rangeText, mono: true },
      ],
    },
    {
      key: 'method',
      title: t('stationVolumes.panel.detailsMethod'),
      testId: 'sv-details-method',
      rows: [
        {
          key: 'method',
          value: (
            <span
              data-testid='sv-method'
              className='sv-num'
              title={t(
                kind === 'deviation' ? 'stationVolumes.panel.methodHelpDeviation' : 'stationVolumes.panel.methodHelp',
                {
                  res: resText,
                },
              )}
            >
              {t('stationVolumes.panel.method', {
                interval: p.stationIntervalM,
                left: p.swathLeftM,
                right: p.swathRightM,
                res: resText,
              })}
            </span>
          ),
        },
        { key: 'footnote', value: t('stationVolumes.panel.footnote', { res: resText }), testId: 'sv-footnote' },
      ],
    },
    {
      key: 'conventions',
      title: t('stationVolumes.panel.detailsConventions'),
      testId: 'sv-details-conventions',
      rows: [
        {
          key: 'corridor',
          value: t('stationVolumes.panel.corridorOnly', { left: p.swathLeftM, right: p.swathRightM }),
          testId: 'sv-corridor-only',
        },
        { key: 'offset', value: t('stationVolumes.panel.offsetConvention'), testId: 'sv-offset-convention' },
        // The OTHER convention a reader has to know before quoting these
        // figures, and the counter-intuitive one: on a deviation run a
        // positive deviation is material ABOVE the design, which this dock
        // counts as cut — the opposite sign to the deviation panel's own
        // volumes. Said in full, beside the offset one (A8).
        ...(kind === 'deviation'
          ? [{ key: 'cut', value: t('stationVolumes.panel.cutConventionDeviation'), testId: 'sv-cut-convention' }]
          : []),
      ],
    },
  ];

  // ---- Tab bar ------------------------------------------------------------

  // `disabledReason` is the layout gate, `lockedGate` the subscription one.
  // Both mute the tab and keep `aria-selected` off it, but a locked tab still
  // takes the click — it opens the upgrade dialog. Only the layout case is
  // inert. The Section and Strip tabs are the two gated panes (spec §4g).
  const tabs: DockTab<StationVolumeTab>[] = [
    {
      id: 'chart',
      label: t('stationVolumes.panel.tabChart'),
      disabledReason: chartTooNarrow ? t('stationVolumes.panel.chartTooNarrow') : null,
    },
    { id: 'table', label: t('stationVolumes.panel.tabTable') },
    { id: 'section', label: t('stationVolumes.panel.tabSection'), lockedGate: gate.allowed ? null : gate.gate },
    { id: 'strip', label: t('stationVolumes.panel.tabStrip'), lockedGate: gate.allowed ? null : gate.gate },
  ];

  /**
   * Section-scoped, so it is in the Section tab's contextual slot and nowhere
   * else — "Overlays" says nothing about a volume chart or a table. It lives
   * in the tab bar rather than inside the chart because the chart's own
   * control strip is behind its `drawn` guard: a run whose overlays have
   * never been sampled would then have no way to configure them.
   */
  const contextualTools =
    dock.tab === 'section' ? (
      <>
        {/* The chart's own vertical-exaggeration picker lands here (a portal
            from `StationSectionChart`): drawing controls belong with the tab
            they draw, and in the chart's row the ten-stop ladder wrapped. */}
        <div ref={setSectionToolsEl} data-testid='sv-section-tools' className='flex items-center gap-1.5' />
        <SectionOverlaysPopover
          layerId={dock.layerId}
          layers={layers}
          sourceHash={meta.alignment.sourceHash ?? null}
          settings={overlaySettings}
          states={overlayStatesById}
          busy={overlaysBusy}
          // "The server has not sampled THIS list." Answerable from the layer
          // row, so Re-sample is reachable before the Section tab ever fetched.
          stale={overlaysStale}
          onChange={(nextOverlays) => void onOverlaysChange(nextOverlays)}
          onResample={() => void onOverlaysResample()}
        />
      </>
    ) : null;

  const walkReason = walkActive ? null : t('stationVolumes.section.walkHint');

  /** View ▾ — the 3D bands, their key and pin, and the two walk switches. */
  const viewMenu = (
    <DockViewMenu testId='sv-view' active={gate.allowed && bandsOn}>
      <DockMenuSection title={t('stationVolumes.panel.view3d')}>
        {/* Gated, never `disabled` (CLAUDE.md): locked it renders off, stays
            focusable and answers a click with the paywall. */}
        <DockMenuSwitch
          testId='sv-bands'
          label={t('stationVolumes.panel.bands')}
          description={t('stationVolumes.panel.bandsHelp')}
          checked={bandsOn}
          gate={gate}
          onCheckedChange={(c) => {
            setBandsOn(c);
            // Bands switched off are not "kept" either: a pin left behind
            // would bring them back the moment the dock closes.
            if (!c && bandsPinned) setPinnedBandsLayerId(null);
          }}
        >
          {/* The key sits under the switch that summons what it describes,
              at the dock's 11 px meta size (audit A9). No "alignment changed"
              line: the header's `sv-stale-alignment` chip already says it. */}
          {bins.length > 0 && (
            <StationBandsLegend variant='menu' bins={bins} lowCoverage={meta.summary.lowCoverageBinCount > 0} />
          )}
        </DockMenuSwitch>
        {/* Only offered while the bands are on — it keeps THESE bands once
            the dock closes, so with them off it would mean nothing. */}
        {gate.allowed && bandsOn && (
          <DockMenuSwitch
            testId='sv-keep-bands'
            label={t('stationVolumes.panel.keepBands')}
            description={t('stationVolumes.panel.keepBandsHelp')}
            checked={bandsPinned}
            onCheckedChange={(c) => setPinnedBandsLayerId(c ? dock.layerId : null)}
          />
        )}
      </DockMenuSection>
      <DockMenuSection title={t('stationVolumes.panel.viewWalk')}>
        {/* Two reasons this can be unavailable, answered differently: no walk
            is a DATA condition (inert, the reason under the label), a locked
            tier is a PAYWALL (still clickable). Clip drives the shared clip
            box, so whichever of the two gates is shut is the one it names. */}
        <DockMenuSwitch
          testId='sv-clip-to-bin'
          label={t('stationVolumes.section.clipToBin')}
          description={t('stationVolumes.section.clipToBinHelp')}
          checked={clipToBin}
          gate={gate.allowed ? clipGate : gate}
          disabledReason={walkReason}
          onCheckedChange={(c) => setClipToBin(c)}
        />
        <DockMenuSwitch
          testId='sv-follow-station'
          label={t('stationVolumes.section.followStation')}
          description={t('stationVolumes.section.followStationHelp')}
          checked={walkActive && jumpToStation}
          disabledReason={walkReason}
          onCheckedChange={() => toggleJumpToStation()}
        />
      </DockMenuSection>
    </DockViewMenu>
  );

  /** Export ▾ — every file this run can be written out as, unchanged. */
  const exportMenu = (
    <DockExportMenu testId='sv-export'>
      {/* A stack exports its OWN two documents (the compliance table and the
          per-station thicknesses), not the station-volumes file: that one
          carries no thickness and no verdict for a stack. */}
      {!isSharedLink && (
      <>
      {stackMeta ? (
        <>
          <DockMenuItem
            data-testid='sv-export-stack-layers-csv'
            disabled={!stackDoc}
            onSelect={() => exportStackCsv('layers')}
          >
            {t('stationVolumes.export.stackLayersCsv')}
          </DockMenuItem>
          <DockMenuItem
            data-testid='sv-export-stack-stations-csv'
            aria-disabled={!stackHasStationRows || undefined}
            disabled={!stackHasStationRows}
            onSelect={() => exportStackCsv('stations')}
            className='flex-col items-start gap-0.5'
          >
            <span>{t('stationVolumes.export.stackStationsCsv')}</span>
            {!!stackDoc && !stackHasStationRows && (
              <span className={DOCK_TEXT.metaMuted}>{t('stationVolumes.export.stackStationsCsvUnavailable')}</span>
            )}
          </DockMenuItem>
        </>
      ) : (
        <>
          <DockMenuItem data-testid='sv-export-csv-all' disabled={!json} onSelect={() => exportCsv(false)}>
            {t('stationVolumes.export.csvAll')}
          </DockMenuItem>
          <DockMenuItem
            data-testid='sv-export-csv-selection'
            disabled={!json || !sel}
            onSelect={() => exportCsv(true)}
            className='flex-col items-start gap-0.5'
          >
            <span>{t('stationVolumes.export.csvSelection')}</span>
            {!sel && <span className={DOCK_TEXT.metaMuted}>{t('stationVolumes.export.csvSelectionPending')}</span>}
          </DockMenuItem>
        </>
      )}
      <DockMenuItem
        data-testid='sv-export-sections-csv'
        aria-disabled={!hasStationLines || undefined}
        disabled={!hasStationLines}
        onSelect={() => exportSectionsCsv(false)}
      >
        {t('stationVolumes.export.sectionsCsvAll')}
      </DockMenuItem>
      <DockMenuItem
        data-testid='sv-export-sections-csv-selection'
        aria-disabled={!hasStationLines || !sel || undefined}
        disabled={!hasStationLines || !sel}
        onSelect={() => exportSectionsCsv(true)}
      >
        <span>{t('stationVolumes.export.sectionsCsvSelection')}</span>
        {!sel && <span className='ml-2 text-[11px] opacity-70'>{t('stationVolumes.export.csvSelectionPending')}</span>}
      </DockMenuItem>
      </>
      )}
      <DockMenuItem data-testid='sv-export-copy' disabled={!json} onSelect={copyTsv}>
        {sel ? t('stationVolumes.export.copySelection') : t('stationVolumes.export.copyAll')}
      </DockMenuItem>
      {!isSharedLink && (
      <>
      {/* `disabled={!json}` on these is a LOADING guard, not the subscription
          gate: it keeps an item inert until `station_volumes.json` has
          arrived, so an early click cannot read an empty `bins` array as "no
          station has measured data". The report's own paywall lives in the
          `onOpenPdfReport` handler and must prompt rather than swallow the
          click (CLAUDE.md / ruling A9), which this guard does not touch. */}
      <DockMenuSeparator />
      <DockMenuItem
        data-testid='sv-export-sections-dxf'
        aria-disabled={!canExportDxf || undefined}
        disabled={!canExportDxf}
        onSelect={openSectionDxf}
      >
        {t('stationVolumes.export.sectionsDxf')}
      </DockMenuItem>
      {/* The same picture the Strip map tab draws, composed offscreen from the
          data, so the file is identical whether or not the tab was opened
          (strip-map spec §3.5). The sub-label is the "this run has no offsets
          AT ALL" reason only — not "not fetched yet". */}
      <DockMenuItem
        data-testid='sv-export-strip-png'
        aria-disabled={!offsets || undefined}
        disabled={!offsets}
        onSelect={() => void exportStripPng()}
        className='flex-col items-start gap-0.5'
      >
        <span>{t('stationVolumes.export.stripPng')}</span>
        {!meta.byproducts?.offsets && (
          <span className={DOCK_TEXT.metaMuted}>{t('stationVolumes.export.stripPngUnavailable')}</span>
        )}
      </DockMenuItem>
      {/* A stack CAN print per-station pages, but only with an alignment, a
          sections byproduct and a MEASURED active layer — each refusal named
          on the item (`perStationPdfStackBlock`, mirrored server-side). */}
      <DockMenuItem
        data-testid='sv-export-per-station-pdf'
        aria-disabled={!json || !!stackBlock || undefined}
        disabled={!json || !!stackBlock}
        onSelect={openPerStationPdf}
        className='flex-col items-start gap-0.5'
      >
        <span>{t('stationVolumes.export.perStationPdf')}</span>
        {!!stackBlock && stackBlock !== 'loading' && (
          <span className={DOCK_TEXT.metaMuted}>{t(`stationVolumes.export.perStationPdfStack_${stackBlock}`)}</span>
        )}
      </DockMenuItem>
      {/* The stack's compliance report (spec §7) — inert only until
          `stack.json` lands, because the dialog's scope line quotes counts
          read off it. */}
      {stackMeta && (
        <DockMenuItem
          data-testid='sv-export-layer-stack-pdf'
          aria-disabled={!stackDoc || undefined}
          disabled={!stackDoc}
          onSelect={openLayerStackPdf}
        >
          {t('stationVolumes.export.layerStackPdf')}
        </DockMenuItem>
      )}
      </>
      )}
    </DockExportMenu>
  );

  /**
   * ⋯ — the actions that are not a file: re-running the analysis, and the PDF
   * report(s) as a second route to the same items Export carries. A stack has
   * no single re-run dialog, so it gets no re-run here (the empty state never
   * offered it one either).
   */
  const overflowMenu = isSharedLink ? null : (
    <DockOverflowMenu testId='sv-more'>
      {!stackMeta && (
        <DockMenuItem
          data-testid='sv-more-rerun'
          disabled={!canRerun || rerunPending}
          aria-busy={rerunPending || undefined}
          onSelect={() => void openRerun()}
          className='flex-col items-start gap-0.5'
        >
          <span className='inline-flex items-center gap-1.5'>
            {rerunPending && <Loader2 aria-hidden className='size-3.5 animate-spin' />}
            {t('stationVolumes.empty.rerun')}
          </span>
          {!canRerun && <span className={DOCK_TEXT.metaMuted}>{t('stationVolumes.empty.rerunUnavailable')}</span>}
        </DockMenuItem>
      )}
    </DockOverflowMenu>
  );

  return (
    <DockFrame
      ref={setRootEl}
      testId='station-volume-dock'
      testIdPrefix='sv'
      ariaLabel={t('stationVolumes.panel.title')}
      height={dock.height}
      onHeightChange={(height) => setDock({ layerId: dock.layerId, tab: dock.tab, height })}
      resizeLabel={t('stationVolumes.panel.resize')}
      resizeHelp={t('stationVolumes.panel.resizeHelp')}
      expanded={expanded}
      // Escape peels one layer at a time: the brush first (it is the thing the
      // user most likely wants back), then the dock.
      onEscape={() => (sel ? setSelection(null) : close())}
      // Opening the dock moves focus into it: Escape has to work on the first
      // press, and a screen-reader user has to land inside what appeared.
      focusKey={dock.layerId}
      // Code review #3: a grip drag in flight when the slot moves to another
      // run must end there, or its next pointermove writes the dock back open.
      // (Closing unmounts the frame, which ends it too.)
      dragResetKey={dock.layerId}
    >
      <DockHeader
        testId='sv-header'
        identity={
          <DockIdentity
            eyebrow={t('stationVolumes.panel.title')}
            title={meta.alignment.name}
            scope={analysisText || undefined}
            scopeTestId='sv-analysis'
            range={rangeText}
            rangeTestId='sv-range'
            rangeTitle={t('stationVolumes.panel.rangeLabel')}
          />
        }
        warnings={warningChips.length > 0 ? warningChips : null}
        kpis={<DockKpiStrip items={kpiItems} testId='sv-kpis' />}
        buttons={
          <DockPanelButtons
            testIdPrefix='sv'
            expanded={expanded}
            onToggleExpanded={() => setExpanded(!expanded)}
            onClose={close}
            closeLabel={t('stationVolumes.panel.close')}
            details={
              <DockDetailsPopover
                triggerTestId='sv-params-toggle'
                testId='sv-details'
                title={meta.alignment.name}
                sections={detailsSections}
                kpis={kpiItems}
              />
            }
          />
        }
      />

      {/* ---- The stack's compliance strip (spec §6.2) ----
              One chip per LAYER and the roll-up beside them, on its own row
              above the tabs: it is both the verdict summary and the selector
              for everything below. Rendered from the LAYER'S METADATA, so it
              is on screen before the stack document has been fetched. Absent
              entirely for a single-pair run. */}
      {stackMeta && stackChips.length > 0 && (
        <div className='flex shrink-0 items-center gap-3 border-t border-slate-200 px-3 py-1.5 dark:border-stone-700'>
          <LayerComplianceStrip
            layers={stackChips}
            rollUp={stackDoc?.rollUp ?? stackMeta.rollUp ?? 'PENDING'}
            activeIndex={activeLayerIndex}
            onSelect={setActiveLayerIndex}
            // The criteria only exist once the document is fetched: layer
            // metadata carries verdicts, never the measurement blocks.
            rollUpCriteria={stackRollUpCriteria}
          />
        </div>
      )}

      <DockTabBar
        testIdPrefix='sv'
        ariaLabel={t('stationVolumes.panel.title')}
        tabs={tabs}
        activeId={dock.tab}
        onSelect={(tab) => setDock({ layerId: dock.layerId, tab, height: dock.height })}
        onLockedClick={() => gate.promptUpgrade()}
        start={
          (chartTooNarrow || selActive) && (
            <>
              {chartTooNarrow && (
                <span data-testid='sv-tab-hint' className={`truncate ${DOCK_TEXT.metaMuted} @max-[560px]:hidden`}>
                  {t('stationVolumes.panel.chartTooNarrow')}
                </span>
              )}
              {/* Only while a brush is active: every KPI silently narrows to
                  it, so it has to be visible and reversible from the chrome. */}
              {selActive && (
                <DockSelectionChip
                  range={selectionText}
                  onClear={() => setSelection(null)}
                  testId='sv-selection'
                  clearTestId='sv-clear-selection'
                />
              )}
            </>
          )
        }
        right={
          <>
            {contextualTools && (
              <>
                <DockToolbar ariaLabel={t('stationVolumes.panel.tabSection')} testId='sv-tab-tools'>
                  {contextualTools}
                </DockToolbar>
                <DockToolbarSeparator />
              </>
            )}
            <DockToolbar>
              {viewMenu}
              {exportMenu}
              {overflowMenu}
            </DockToolbar>
          </>
        }
      />

      {/* ---- Body: the ONE row that flexes. `overflow-hidden`, not `auto`:
              every view in here sizes itself to this box (the chart and the
              cross-section measure it with a ResizeObserver, the table scrolls
              its own rows). ---- */}
      <DockBody id='sv-tabpanel' labelledBy={`sv-tab-${dock.tab}`}>
        {error && (
          <div className='flex h-full flex-col items-start gap-2 overflow-auto'>
            {/* LoadingBadge renders the raw failure detail; the headline above
                it is what tells the reader which thing failed. */}
            <p className='text-sm font-semibold'>{t('stationVolumes.errors.failed')}</p>
            <LoadingBadge
              name={badgeName}
              phase='failed'
              error={schemaTooNew ? t('stationVolumes.errors.schemaTooNew') : error.message}
              labels={{ failed: t('stationVolumes.errors.failed') }}
            />
            {schemaTooNew ? (
              rerunControl
            ) : (
              <Button data-testid='sv-retry' type='button' size='sm' variant='outline' onClick={refetch}>
                {t('stationVolumes.errors.retry')}
              </Button>
            )}
          </div>
        )}
        {!error && isLoading && !json && (
          <LoadingBadge
            name={badgeName}
            phase={progress?.phase ?? 'fetching'}
            progress={progress?.totalBytes ? progress.loadedBytes / progress.totalBytes : undefined}
            loadedBytes={progress?.loadedBytes}
            totalBytes={progress?.totalBytes ?? undefined}
            labels={{
              fetching: t('stationVolumes.errors.fetching'),
              decoding: t('stationVolumes.errors.decoding'),
              failed: t('stationVolumes.errors.failed'),
            }}
          />
        )}
        {!error && isEmpty && (
          <div className='flex h-full flex-col items-start gap-2 overflow-auto py-4' data-testid='sv-empty'>
            {/* A PENDING layer of a stack is not an empty measurement — it is
                a layer nobody has built yet, and saying "nothing was measured
                in the corridor" would read as a fault in the analysis. */}
            <p className='text-sm font-semibold'>
              {stackPending
                ? t('layerStack.dock.pendingLayerTitle')
                : t(kind === 'deviation' ? 'stationVolumes.empty.titleDeviation' : 'stationVolumes.empty.title')}
            </p>
            <p className='text-slate-500 dark:text-stone-400'>
              {stackPending ? t('layerStack.dock.pendingLayerBody') : t('stationVolumes.empty.body')}
            </p>
            {!stackMeta && rerunControl}
          </div>
        )}
        {!error && hasData && json && (
          /* `data-layer` on all FOUR panes, not on the dock: the chip strip
             selects one layer of the stack and every view has to move with it.
             `undefined` — the attribute absent — on a single-pair run. */
          <>
            {dock.tab === 'chart' && (
              <div className='h-full min-h-0' data-testid='sv-chart-pane' data-layer={stackLayerAttr}>
                <StationVolumeChart
                  json={json}
                  layerId={dock.layerId}
                  width={width}
                  compact={width < DOCK_NARROW_PX}
                  kind={kind}
                />
              </div>
            )}
            {dock.tab === 'table' && (
              <div className='h-full min-h-0' data-testid='sv-table' data-layer={stackLayerAttr}>
                <StationVolumeTable json={json} layerId={dock.layerId} kind={kind} />
              </div>
            )}
            {dock.tab === 'section' && (
              /* The surface names live on the layer's metadata, not in the
                 bins file, so the section gets them from here — the same
                 place the CSV header and the dock title read them from. */
              <div className='h-full min-h-0' data-testid='sv-section' data-layer={stackLayerAttr}>
                <StationSectionChart
                  json={json}
                  meta={meta}
                  layerId={dock.layerId}
                  batchId={batchId}
                  kind={kind}
                  topLayerName={topName || undefined}
                  bottomLayerName={bottomName || undefined}
                  expectation={sectionExpectation}
                  // Which two of the file's 2N planes bound the selected
                  // layer. Without this every chip would draw layer 0.
                  planePair={stack?.planePair ?? null}
                  // A stack carries one pair of tolerances per LEVEL, and the
                  // section marks each band against its own.
                  levelTolerances={stack?.levelTolerances ?? null}
                  // What the server last sampled, what the user has configured
                  // NOW, and the dock's own names for the ones no file
                  // describes yet — their disagreement IS the pending state.
                  overlaysMeta={overlaysMeta}
                  overlaySettings={overlaySettings}
                  overlayLabels={overlayLabels}
                  toolbarSlot={sectionToolsEl}
                />
              </div>
            )}
            {dock.tab === 'strip' && (
              <div className='h-full min-h-0' data-testid='sv-strip-pane' data-layer={stackLayerAttr}>
                <StripMapTab
                  json={json}
                  meta={meta}
                  layerId={dock.layerId}
                  batchId={batchId}
                  kind={kind}
                  expectation={sectionExpectation}
                  stack={stack ?? null}
                />
              </div>
            )}
          </>
        )}
      </DockBody>
    </DockFrame>
  );
};
